import React, { useState, useRef, useEffect, useMemo } from "react"
import ReactDOM from "react-dom/client"
import { XMLParser } from 'fast-xml-parser';

// Prevent multiple initializations
if (window.istqbTrainingProviderInitialized) {
  console.log("ISTQB Training Provider component already initialized, skipping...");
} else {
  window.istqbTrainingProviderInitialized = true;
  
  // Clean up any potential duplicates first
  const existingComponents = document.querySelectorAll('.istqb-training-provider-wrapper');
  if (existingComponents.length > 0) {
    console.log('Found existing components, cleaning up:', existingComponents.length);
    existingComponents.forEach(component => component.remove());
  }
  
  // Log the number of targets found for debugging - use more specific selector
  const divsToUpdate = document.querySelectorAll(".plugitintrainingsoap-render-target:not([data-istqb-initialized='true'])")
  console.log("Found divs to update:", divsToUpdate.length)
  console.log("Divs found:", Array.from(divsToUpdate).map((div, index) => ({
    index,
    className: div.className,
    id: div.id,
    initialized: div.dataset.istqbInitialized
  })))

  // Only render if we have legitimate targets - no fallback creation
  if (divsToUpdate.length === 0) {
    console.log("No valid render targets found. Component will only render where explicitly placed via blocks or shortcodes.")
  } else {
    // Process existing divs
    divsToUpdate.forEach(div => initializeComponent(div))
  }
}

// Function to initialize the component in a target div
function initializeComponent(div) {
  try {
    // Check if this div has already been processed
    if (div.dataset.istqbInitialized === 'true') {
      console.log('Div already initialized, skipping...');
      return;
    }
    
    // Mark this div as being processed
    div.dataset.istqbInitialized = 'true';
    
    div.style.width = '100%'
    div.style.maxWidth = '100%'
    
    // Get the pre element content
    const preElement = div.querySelector("pre");
    if (!preElement) {
      console.error('No pre element found in target div');
      return;
    }
    
    const preContent = preElement.innerText
    
    // Clean the JSON string before parsing
    const cleanedContent = preContent
      .replace(/\\([^"])/g, '\\\\$1') // Escape backslashes except those before quotes
      .replace(/\n/g, '\\n')          // Handle newlines
      .replace(/\r/g, '\\r')          // Handle carriage returns
      .replace(/\t/g, '\\t')          // Handle tabs
      .replace(/\f/g, '\\f')          // Handle form feeds
      
    // Parse the cleaned JSON
    let data
    try {
      data = JSON.parse(cleanedContent)
    } catch (jsonError) {
      console.error("Failed to parse JSON:", jsonError)
      console.log("Content that failed to parse:", cleanedContent)
      
      // Use default data if parsing fails
      data = {
        xmlData: null,
        pluginUrl: window.location.origin + '/wp-content/plugins/plug-it-In-trainingsoap'
      }
    }
    
    data.xmlData = null // We'll fetch from API by default
    
    // Use a try-catch specifically for the ReactDOM.createRoot call
    try {
      console.log('Creating React root for div:', div.className, div.id);
      const root = ReactDOM.createRoot(div)
      root.render(<OurComponent {...data} />)
      div.classList.remove("plugitintrainingsoap-render-target")
      console.log('Successfully rendered React component');
    } catch (reactError) {
      console.error("React rendering error:", reactError)
      div.innerHTML = '<div class="p-4 bg-red-100 border border-red-400 text-red-700 rounded">' +
        'Error loading ISTQB Training Provider component. Please contact the administrator.' +
        '</div>'
    }
  } catch (error) {
    console.error('Error initializing component:', error)
    if (div.querySelector("pre")) {
      console.log('Content that failed to parse:', div.querySelector("pre").innerText)
    } else {
      console.log('No pre element found in the target div')
    }
  }
}

function parseXMLData(xmlString) {
  const options = {
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    removeNSPrefix: true, // This removes namespace prefixes like ns1:, a:, etc.
    parseAttributeValue: true,
    parseTagValue: true,
    trimValues: true,
    isArray: (name, jpath, isLeafNode, isAttribute) => {
      const arrayElements = [
        'TrainingProviderWrapper',
        'TrainingProviderCourseWrapper',
        'LanguageWrapper'
      ];
      return arrayElements.indexOf(name) !== -1;
    }
  };

  const parser = new XMLParser(options);
  
  try {
    // First try to decode JSON if the string is JSON-encoded
    let decodedXml = xmlString;
    try {
      const jsonData = JSON.parse(xmlString);
      if (typeof jsonData === 'string') {
        // If it's a JSON string, decode the escaped characters
        decodedXml = jsonData
          .replace(/\\"/g, '"')
          .replace(/\\\//g, '/')
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t');
      }
    } catch (e) {
      // If JSON.parse fails, use the original string
      console.log('Not JSON encoded, using raw XML');
    }

    // Now parse the XML
    const result = parser.parse(decodedXml);
    console.log('🔍 Raw parsed XML result keys:', Object.keys(result));
    
    // Navigate through SOAP envelope to get to providers
    const soapBody = result?.Envelope?.Body;
    const providerList = soapBody?.GetTrainingProviderListResponse?.GetTrainingProviderListResult;
    
    if (!providerList?.TrainingProviderWrapper) {
      console.error('No providers found in XML');
      return { providers: [], certifications: [] };
    }

    console.log('🔍 Found', providerList.TrainingProviderWrapper.length, 'providers');
    console.log('🔍 First provider raw structure:', providerList.TrainingProviderWrapper[0]);
    console.log('🔍 First provider keys:', Object.keys(providerList.TrainingProviderWrapper[0] || {}));

    // Helper function to safely convert XML nil values
    const sanitizeValue = (value) => {
      // Check if the value is an object with @_nil property
      if (value && typeof value === 'object' && '@_nil' in value) {
        return '';
      }
      return value || '';
    };

    const providers = providerList.TrainingProviderWrapper.map((provider, index) => {
      if (index < 3) { // Only debug first 3 providers in detail
        console.log(`🔍 Processing provider ${index}:`, provider.Name);
        console.log(`🔍 Provider structure keys:`, Object.keys(provider));
        console.log(`🔍 Provider AccreditatedBoard:`, provider.AccreditatedBoard);
        console.log(`🔍 TrainingProviderCourseList:`, provider.TrainingProviderCourseList);
        console.log(`🔍 TrainingProviderCourseWrapper inside list:`, provider.TrainingProviderCourseList?.TrainingProviderCourseWrapper);
      }
      
      // Debug course structure - Fix the path to courses
      const courses = (provider.TrainingProviderCourseList?.TrainingProviderCourseWrapper || []).map(course => {
        if (index < 3) { // Only debug first 3 providers in detail
          console.log(`🔍 Processing course:`, {
            courseStructure: course,
            courseKeys: Object.keys(course || {}),
            certificationName: course.CertificationType?.Name,
            languagesStructure: course.Languages,
            languageWrapper: course.Languages?.LanguageWrapper
          });
        }
        
        // Extract languages - handle the correct XML structure: Languages.LanguageWrapper
        let languages = [];
        if (course.Languages?.LanguageWrapper) {
          if (Array.isArray(course.Languages.LanguageWrapper)) {
            // Multiple languages
            languages = course.Languages.LanguageWrapper.map(lang => sanitizeValue(lang.NameLanguage)).filter(lang => lang);
          } else {
            // Single language
            const langName = sanitizeValue(course.Languages.LanguageWrapper.NameLanguage);
            if (langName) languages.push(langName);
          }
        }
        
        // Extract training categories - handle TrainingCategory.TrainingCategoryWrapper structure
        let trainingCategories = [];
        if (course.TrainingCategory?.TrainingCategoryWrapper) {
          if (Array.isArray(course.TrainingCategory.TrainingCategoryWrapper)) {
            // Multiple training categories
            trainingCategories = course.TrainingCategory.TrainingCategoryWrapper.map(category => ({
              name: sanitizeValue(category.Name),
              countriesOfOperation: sanitizeValue(category.CountriesOfOperation)
            })).filter(cat => cat.name);
          } else {
            // Single training category
            const categoryName = sanitizeValue(course.TrainingCategory.TrainingCategoryWrapper.Name);
            const countriesOfOperation = sanitizeValue(course.TrainingCategory.TrainingCategoryWrapper.CountriesOfOperation);
            if (categoryName) trainingCategories.push({ name: categoryName, countriesOfOperation });
          }
        }
        
        if (index < 3) { // Only debug first 3 providers in detail
          console.log(`🔍 Extracted languages for course "${course.CertificationType?.Name}":`, languages);
          console.log(`🔍 Extracted training categories for course "${course.CertificationType?.Name}":`, trainingCategories);
          console.log(`🔍 Extracted accredited board for course "${course.CertificationType?.Name}":`, {
            nameBoard: sanitizeValue(course.AccreditatedBoard?.NameBoard),
            countriesOfOperation: sanitizeValue(course.AccreditatedBoard?.CountriesOfOperation)
          });
        }
        
        return {
          name: sanitizeValue(course.CertificationType?.Name),
          acronym: sanitizeValue(course.CertificationType?.Acronym),
          id: sanitizeValue(course.Id),
          type: sanitizeValue(course.MaterialType),
          languages: languages,
          trainingCategories: trainingCategories,
          accreditatedBoard: {
            nameBoard: sanitizeValue(course.AccreditatedBoard?.NameBoard),
            countriesOfOperation: sanitizeValue(course.AccreditatedBoard?.CountriesOfOperation)
          }
        };
      });
      
      if (index < 3) { // Only debug first 3 providers in detail
        console.log(`🔍 Final courses for provider ${provider.Name}:`, courses);
        console.log(`🔍 Courses length:`, courses.length);
      }
      
      return {
      name: sanitizeValue(provider.Name),
      country: sanitizeValue(provider.Country?.ShortName) || sanitizeValue(provider.Country?.LongName),
      email: sanitizeValue(provider.Email).split(/\s+or\s+/)[0],
      website: sanitizeValue(provider.WebSite),
      address: sanitizeValue(provider.Address),
      city: sanitizeValue(provider.City),
      phone: sanitizeValue(provider.Phone),
      referencePerson: sanitizeValue(provider.ReferencePerson),
      accreditatedBoard: {
        nameBoard: sanitizeValue(provider.AccreditatedBoard?.NameBoard)
      },
        courses: courses
      };
    });

    console.log('🔍 Final processed providers sample:', providers.slice(0, 2));

    return {
      providers: providers,
      certifications: providers[0]?.courses || []
    };
    
  } catch (error) {
    console.error('Error parsing XML:', error);
    console.error('XML content:', xmlString.substring(0, 1000));
    return { providers: [], certifications: [] };
  }
}

function truncateText(text, wordLimit = 6, charLimit = 30) {
  // First check character length
  if (text.length > charLimit) {
    return text.substring(0, charLimit) + '...'
  }
  
  // Then check word count
  const words = text.split(' ')
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ') + '...'
  }
  
  return text
}

const ClearButton = ({ onClick, show }) => {
  if (!show) return null;
  
  return (
    <button 
      onClick={onClick}
      className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
      title="Clear selection"
      type="button"
      style={{ zIndex: 1 }}
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M6 18L18 6M6 6l12 12" 
        />
      </svg>
    </button>
  );
};

const Loader = () => (
  <div className="flex justify-center items-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const DropdownLoader = () => (
  <div className="px-4 py-2 text-gray-500 text-sm">
    <div className="flex items-center space-x-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      <span>Loading...</span>
    </div>
  </div>
);

// Add JSON Tree View Component
const JsonTreeView = ({ data, name = "root", level = 0, searchTerm = "" }) => {
  const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first 2 levels
  
  const indent = level * 20;
  
  if (data === null || data === undefined) {
    return (
      <div style={{ marginLeft: `${indent}px` }} className="text-gray-500 py-1">
        <span className="text-blue-600 font-medium">{name}:</span> <span className="italic">null</span>
      </div>
    );
  }
  
  if (typeof data !== 'object') {
    const value = String(data);
    const isHighlighted = searchTerm && value.toLowerCase().includes(searchTerm.toLowerCase());
    
    return (
      <div style={{ marginLeft: `${indent}px` }} className="py-1">
        <span className="text-blue-600 font-medium">{name}:</span>{' '}
        <span className={`${isHighlighted ? 'bg-yellow-200' : ''} text-gray-800`}>
          "{value}"
        </span>
      </div>
    );
  }
  
  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((item, index) => [index, item]) : Object.entries(data);
  const hasChildren = entries.length > 0;
  
  // Filter entries based on search term
  const filteredEntries = searchTerm 
    ? entries.filter(([key, value]) => {
        const keyStr = String(key).toLowerCase();
        const valueStr = JSON.stringify(value).toLowerCase();
        return keyStr.includes(searchTerm.toLowerCase()) || 
               valueStr.includes(searchTerm.toLowerCase());
      })
    : entries;
  
  const shouldShow = !searchTerm || filteredEntries.length > 0 || 
                     name.toLowerCase().includes(searchTerm.toLowerCase());
  
  if (!shouldShow) return null;
  
  return (
    <div className="font-mono text-sm">
      <div 
        style={{ marginLeft: `${indent}px` }} 
        className="py-1 cursor-pointer hover:bg-gray-100 flex items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {hasChildren && (
          <span className="mr-2 text-gray-500 select-none">
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        <span className="text-purple-600 font-medium">{name}</span>
        <span className="text-gray-500 ml-2">
          {isArray ? `[${data.length}]` : `{${entries.length}}`}
        </span>
      </div>
      
      {isExpanded && hasChildren && (
        <div>
          {(searchTerm ? filteredEntries : entries).map(([key, value]) => (
            <JsonTreeView 
              key={key}
              data={value} 
              name={key} 
              level={level + 1}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Add Dev Panel Component
const DevPanel = ({ isOpen, onClose, data }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("tree");
  
  if (!isOpen) return null;
  
  const jsonString = JSON.stringify(data, null, 2);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-800">XML Data Explorer</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedTab("tree")}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  selectedTab === "tree" 
                    ? "bg-blue-500 text-white" 
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Tree View
              </button>
              <button
                onClick={() => setSelectedTab("raw")}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  selectedTab === "raw" 
                    ? "bg-blue-500 text-white" 
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Raw JSON
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Search Bar */}
        {selectedTab === "tree" && (
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search in data..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {selectedTab === "tree" ? (
            <div className="bg-gray-50 rounded-lg p-4">
              {data ? (
                <JsonTreeView data={data} name="XML Data" searchTerm={searchTerm} />
              ) : (
                <div className="text-gray-500 text-center py-8">
                  No data available. Load some XML data first.
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm overflow-auto">
              <pre className="whitespace-pre-wrap">{jsonString}</pre>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              {data && data.providers ? (
                <span>📊 {data.providers.length} providers loaded</span>
              ) : (
                <span>📄 Raw XML data structure</span>
              )}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  const jsonBlob = new Blob([jsonString], { type: 'application/json' });
                  const url = URL.createObjectURL(jsonBlob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'istqb-data.json';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                📥 Download JSON
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(jsonString);
                  // You could add a toast notification here
                }}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                📋 Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function useScreenSize(breakpoint = 1000) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]); // Only re-run effect if breakpoint changes

  return isMobile;
}

function OurComponent(props) {
  console.log('🏗️ OurComponent instantiated with props:', props);
  const isMobileLayout = useScreenSize(1000); // Use the hook for all resize handling
  
  // Function to check if we're on an ISTQB domain where XML Explorer should be hidden
  const isISTQBDomain = () => {
    const hostname = window.location.hostname.toLowerCase();
    return hostname === 'istqb.org' || 
           hostname === 'www.istqb.org' || 
           hostname === 'staging.istqb.site';
  };
  
  const [showSkyColor, setShowSkyColor] = useState(false)
  const [showGrassColor, setShowGrassColor] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [countrySearch, setCountrySearch] = useState("")
  const [languageSearch, setLanguageSearch] = useState("")
  const [courseSearch, setCourseSearch] = useState("")
  const [countryOpen, setCountryOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [courseOpen, setCourseOpen] = useState(false)
  const [boardSearch, setBoardSearch] = useState("")
  const [boardOpen, setBoardOpen] = useState(false)
  const [trainingCategorySearch, setTrainingCategorySearch] = useState("")
  const [trainingCategoryOpen, setTrainingCategoryOpen] = useState(false)
  const [materialTypeSearch, setMaterialTypeSearch] = useState("")
  const [materialTypeOpen, setMaterialTypeOpen] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [boardData, setBoardData] = useState(null)
  const [providers, setProviders] = useState([])
  const [courses, setCourses] = useState([])
  const [courseObjects, setCourseObjects] = useState([])
  const [boards, setBoards] = useState([])
  const [useApi, setUseApi] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [trainingCategories, setTrainingCategories] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [rawXmlData, setRawXmlData] = useState(null);

  // Fisher-Yates shuffle algorithm for better randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Define suffix order for acronym-based sorting
  const suffixOrder = [
    'TA', 'TM', 'TTA', 'TAE', 'TM-OTM', 'TM-SM', 'TM-MTT', 'TM-STM', 
    'ITP-ATP', 'ITP-ITPI', 'ATT', 'ATLaS', 'AT', 'AI', 'MAT', 'MBT', 
    'TAS', 'AcT', 'PT', 'SEC', 'UT', 'AuT', 'GT', 'STE', 'GaMe'
  ];

  // Function to extract suffix after first dash in acronym
  const getAcronymSuffix = (course) => {
    if (!course.acronym) return '';
    const dashIndex = course.acronym.indexOf('-');
    return dashIndex !== -1 ? course.acronym.substring(dashIndex + 1) : course.acronym;
  };

  // Clean course name function for fallback sorting
  const cleanCourseName = (name) => {
    let cleanName = name;
    
    // Remove common prefixes and subheadings
    const prefixesToRemove = [
      'Core Foundation ',
      'Core Advanced ',
      'Specialist Technologies & Approaches ',
      'Specialist Quality Characteristics & Test Levels ',
      'Specialist Testing in Particular Domains ',
      'Expert Test Management ',
      'Expert Improving the Testing Process ',  // With "the"
      'Expert Improving Testing Process ',      // Without "the"
      'Istqb Certified Tester ',
      'ISTQB Certified Tester ',
      'Certified Tester ',
      'Advanced Level ',
      'Foundation Level ',
      'ISTQB '
    ];
    
    // Apply prefix removal iteratively (some names might have multiple prefixes)
    let previousLength;
    do {
      previousLength = cleanName.length;
      prefixesToRemove.forEach(prefix => {
        if (cleanName.startsWith(prefix)) {
          cleanName = cleanName.substring(prefix.length);
        }
      });
    } while (cleanName.length < previousLength && cleanName.length > 0);
    
    // Clean up any double spaces and trim
    cleanName = cleanName.replace(/\s+/g, ' ').trim();
    
    // If the name becomes too short or empty, use original name
    if (cleanName.length < 3) {
      cleanName = name;
    }
    
    return cleanName;
  };

  // Reusable custom sorting function based on acronym suffix
  const sortCoursesByAcronymSuffix = (a, b) => {
    const suffixA = getAcronymSuffix(a);
    const suffixB = getAcronymSuffix(b);
    
    const indexA = suffixOrder.indexOf(suffixA);
    const indexB = suffixOrder.indexOf(suffixB);
    
    // If both suffixes are in the order list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    // If only one suffix is in the order list, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    // If neither suffix is in the order list, fall back to alphabetical sorting by course name
    return cleanCourseName(a.name).localeCompare(cleanCourseName(b.name));
  };

  // Function to group courses hierarchically for dropdown
  const groupCoursesForDropdown = (courseObjects) => {
    if (!courseObjects || courseObjects.length === 0) return {};
    
    // Initialize dynamic groups
    const groups = {};
    
      // Helper function to determine certification type (Core/Specialist/Expert)
  const getCertificationType = (course) => {
    const courseName = course.name || '';
    const lowerName = courseName.toLowerCase();
    
    // Check by course name patterns - but exclude Expert courses
    if ((lowerName.includes('core foundation') || 
        lowerName.includes('test analyst') || 
        lowerName.includes('test management') || 
        lowerName.includes('technical test analyst') || 
         lowerName.includes('test automation engineering')) &&
        !lowerName.includes('expert')) {
      return 'Core';
    }
    
    // Expert level courses
    if (lowerName.includes('operational') || 
        lowerName.includes('managing the test team') || 
        lowerName.includes('strategic management') || 
        lowerName.includes('assessing test processes') || 
        lowerName.includes('implementing test process improvement')) {
      return 'Expert';
    }
    
    // Everything else is Specialist by default (since most courses are specialist)
    return 'Specialist';
  };

  // Helper function to determine certification level/category
  const getCertificationLevel = (course) => {
    const courseName = course.name || '';
    const lowerName = courseName.toLowerCase();
    
    // Core levels
    if (lowerName.includes('foundation') || lowerName.includes('ctfl 4.0')) {
      return 'Foundation';
    }
    // Core Advanced - but exclude Expert courses
    if ((lowerName.includes('test analyst') || 
        lowerName.includes('test management') || 
        lowerName.includes('technical test analyst') || 
         lowerName.includes('test automation engineering')) &&
        !lowerName.includes('expert')) {
      return 'Advanced';
    }
    
    // Specialist categories - Technologies & Approaches
    if (lowerName.includes('agile') || 
        lowerName.includes('ai testing') || 
        lowerName.includes('mobile application') || 
        lowerName.includes('model-based') || 
        lowerName.includes('test automation strategy') ||
        lowerName.includes('behavior driven development') ||
        lowerName.includes('continuous testing') ||
        lowerName.includes('test-driven development') ||
        lowerName.includes('exploratory testing') ||
        lowerName.includes('risk-based testing') ||
        lowerName.includes('devops') ||
        lowerName.includes('api testing') ||
        lowerName.includes('microservices testing')) {
      return 'Technologies & Approaches';
    }
    
    // Specialist categories - Quality Characteristics & Test Levels
    if (lowerName.includes('acceptance testing') || 
        lowerName.includes('performance testing') || 
        lowerName.includes('security tester') || 
        lowerName.includes('security test engineer') || 
        lowerName.includes('usability testing') ||
        lowerName.includes('accessibility testing') ||
        lowerName.includes('load testing') ||
        lowerName.includes('stress testing') ||
        lowerName.includes('reliability testing') ||
        lowerName.includes('compatibility testing') ||
        lowerName.includes('system testing') ||
        lowerName.includes('integration testing') ||
        lowerName.includes('unit testing')) {
      return 'Quality Characteristics & Test Levels';
    }
    
    // Specialist categories - Testing in Particular Domains
    if (lowerName.includes('automotive') || 
        lowerName.includes('gambling') || 
        lowerName.includes('game testing') ||
        lowerName.includes('medical device') ||
        lowerName.includes('healthcare') ||
        lowerName.includes('aviation') ||
        lowerName.includes('railway') ||
        lowerName.includes('finance') ||
        lowerName.includes('e-commerce') ||
        lowerName.includes('embedded systems') ||
        lowerName.includes('iot testing') ||
        lowerName.includes('blockchain testing')) {
      return 'Testing in Particular Domains';
    }
    
    // Expert categories - Test Management
    if (lowerName.includes('operational') || 
        lowerName.includes('managing the test team') || 
        lowerName.includes('strategic management') ||
        lowerName.includes('test manager') ||
        lowerName.includes('test leadership') ||
        lowerName.includes('test strategy') ||
        lowerName.includes('test planning') ||
        lowerName.includes('test metrics') ||
        lowerName.includes('resource management')) {
      return 'Test Management';
    }
    
    // Expert categories - Improving Testing Process
    if (lowerName.includes('assessing test processes') || 
        lowerName.includes('implementing test process improvement') ||
        lowerName.includes('process optimization') ||
        lowerName.includes('test maturity') ||
        lowerName.includes('quality assurance') ||
        lowerName.includes('test process assessment') ||
        lowerName.includes('continuous improvement') ||
        lowerName.includes('tpi next') ||
        lowerName.includes('tmmi')) {
      return 'Improving Testing Process';
    }
    
    // Default fallbacks based on type
    const type = getCertificationType(course);
    if (type === 'Core') return 'Foundation';
    if (type === 'Expert') return 'Test Management';
    return 'Technologies & Approaches'; // Default for Specialist
  };
    
      // Define preferred order for types and levels
  const typeOrder = ['Core', 'Specialist', 'Expert'];
  const levelOrder = {
    'Core': ['Foundation', 'Advanced'],
    'Specialist': [
      'Technologies & Approaches',
      'Quality Characteristics & Test Levels', 
      'Testing in Particular Domains'
    ],
    'Expert': [
      'Test Management',
      'Improving Testing Process'
    ]
  };
  
  // Group courses by type and level (Core > Foundation > Course)
  courseObjects.forEach(course => {
    const type = getCertificationType(course);
    const level = getCertificationLevel(course);
    
    // Initialize nested structure
    if (!groups[type]) groups[type] = {};
    if (!groups[type][level]) groups[type][level] = [];
    
    // Add course to appropriate group
    groups[type][level].push(course);
  });
    
  // Sort the groups in the correct order
  const sortedGroups = {};
  
  // Process types in the defined order
  typeOrder.forEach(type => {
    if (groups[type]) {
      sortedGroups[type] = {};
      
      // Process levels in the defined order for this type
      if (levelOrder[type]) {
        levelOrder[type].forEach(level => {
          if (groups[type][level]) {
            sortedGroups[type][level] = groups[type][level];
          }
        });
      }
      
      // Add any additional levels not in the predefined order (for new categories)
      Object.keys(groups[type]).forEach(level => {
        if (!levelOrder[type] || !levelOrder[type].includes(level)) {
          sortedGroups[type][level] = groups[type][level];
          console.log(`🆕 New course category detected: ${type} > ${level}`);
        }
      });
    }
  });
  
  // Add any additional types not in the predefined order (for completely new types)
  Object.keys(groups).forEach(type => {
    if (!typeOrder.includes(type)) {
      sortedGroups[type] = groups[type];
      console.log(`🆕 New course type detected: ${type}`);
    }
  });
  
  // Sort courses within each group alphabetically by cleaned course name
  const cleanCourseName = (name) => {
    let cleanName = name;
    
    // Remove common prefixes and subheadings
    const prefixesToRemove = [
      'Core Foundation ',
      'Core Advanced ',
      'Specialist Technologies & Approaches ',
      'Specialist Quality Characteristics & Test Levels ',
      'Specialist Testing in Particular Domains ',
      'Expert Test Management ',
      'Expert Improving the Testing Process ',  // With "the"
      'Expert Improving Testing Process ',      // Without "the"
      'Certified Tester ',
      'Advanced Level ',
      'Foundation Level ',
      'ISTQB Certified Tester ',
      'ISTQB ',
      'Core ',
      'Specialist ',
      'Expert ',
      'Advanced ',
      'Foundation ',
      'Test ',
      'Testing ',
      '- '
    ];
    
    // Apply prefix removal iteratively (some names might have multiple prefixes)
    let previousLength;
    do {
      previousLength = cleanName.length;
      prefixesToRemove.forEach(prefix => {
        if (cleanName.startsWith(prefix)) {
          cleanName = cleanName.substring(prefix.length);
        }
      });
    } while (cleanName.length < previousLength && cleanName.length > 0);
    
    // Clean up any double spaces and trim
    cleanName = cleanName.replace(/\s+/g, ' ').trim();
    
    // If the name becomes too short or empty, use original name
    if (cleanName.length < 3) {
      cleanName = name;
    }
    
    return cleanName;
  };

  // Sort courses within each group using the new acronym-based sorting
  Object.keys(sortedGroups).forEach(type => {
    Object.keys(sortedGroups[type]).forEach(level => {
      sortedGroups[type][level].sort(sortCoursesByAcronymSuffix);
    });
  });
  
  return sortedGroups;
  };

  const filteredProviders = providers.filter(provider => {
    // Only filter by provider name for the dropdown search
    const matchesProvider = !searchTerm || 
      provider.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // If no other search criteria are active, show all providers that match the name search
    const hasAnyCourseFilters = courseSearch || materialTypeSearch || languageSearch || trainingCategorySearch || countrySearch || boardSearch;
    
    if (!hasAnyCourseFilters) {
      return matchesProvider;
    }
    
    // Apply the same comprehensive filtering logic as the main results
    const matchesCountry = !countrySearch || provider.courses.some(course => {
      // Check training category countries of operation
      const trainingCategoryMatch = course.trainingCategories && course.trainingCategories.some(category => {
        // If searching for a specific training type, only check that type's countries
        if (trainingCategorySearch && trainingCategorySearch.trim() !== '') {
          const matchesTrainingType = category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase());
          if (!matchesTrainingType) return false; // Skip categories that don't match the search
        }
        
        const countriesOfOperation = category.countriesOfOperation || '';
        return countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
      });
      
      // Check accredited board countries of operation (only if no specific training type is selected)
      let accreditedBoardMatch = false;
      if (!trainingCategorySearch || trainingCategorySearch.trim() === '') {
        accreditedBoardMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
          course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
      }
      
      return trainingCategoryMatch || accreditedBoardMatch;
    });
    
    const matchesBoard = !boardSearch || 
      provider.accreditatedBoard?.nameBoard?.toLowerCase().includes(boardSearch.toLowerCase()) ||
      provider.courses.some(course => 
        course.accreditatedBoard?.nameBoard?.toLowerCase().includes(boardSearch.toLowerCase())
      );
    
    // Check if provider has courses matching ALL specified criteria
    let matchesCourseFilters = true;
    
    if (hasAnyCourseFilters) {
      // When any course-related filters are applied, find courses that match ALL criteria
      const matchesAllCriteria = provider.courses.some(course => {
        // Skip courses without languages
        if (!course.languages || course.languages.length === 0) {
          return false;
        }

        if (!course.name || typeof course.name !== 'string') {
          return false;
        }
        
        // Check course name match
        const courseName = course.name.trim();
        const courseNameMatches = !courseSearch || courseName.toLowerCase() === courseSearch.trim().toLowerCase();
        
        // Check material type match
        const materialTypeMatches = !materialTypeSearch || course.type === materialTypeSearch;
        
        // Check language match
        const languageMatches = !languageSearch || 
          course.languages.some(lang => lang.toLowerCase().includes(languageSearch.toLowerCase()));
        
        // Check training category match
        const trainingCategoryMatches = !trainingCategorySearch || 
          (course.trainingCategories && course.trainingCategories.some(category =>
            category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase())
          ));
        
        // Course must match ALL specified criteria
        let courseMatchesAll = courseNameMatches && materialTypeMatches && languageMatches && trainingCategoryMatches;
        
        // If course matches all criteria and we're also filtering by country, check local accreditation for this specific course
        if (courseMatchesAll && countrySearch && countrySearch.trim() !== '') {
          // Check training category countries of operation for this specific course
          const trainingCategoryCountryMatch = course.trainingCategories && course.trainingCategories.some(category => {
            // If searching for a specific training type, only check that type's countries
            if (trainingCategorySearch && trainingCategorySearch.trim() !== '') {
              const matchesTrainingType = category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase());
              if (!matchesTrainingType) return false; // Skip categories that don't match the search
            }
            
            const countriesOfOperation = category.countriesOfOperation || '';
            return countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
          });
          
          // Check accredited board countries of operation for this specific course (only if no specific training type is selected)
          let accreditedBoardCountryMatch = false;
          if (!trainingCategorySearch || trainingCategorySearch.trim() === '') {
            accreditedBoardCountryMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
              course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
          }
          
          // Course must match all criteria AND have local accreditation
          return courseMatchesAll && (trainingCategoryCountryMatch || accreditedBoardCountryMatch);
        }
        
        return courseMatchesAll;
      });
      
      matchesCourseFilters = matchesAllCriteria;
    }
    
    return matchesProvider && matchesCountry && matchesBoard && matchesCourseFilters;
  }).sort((a, b) => a.name.localeCompare(b.name));

  // Log providers in dropdown and their count
  console.log('🎯 Providers in dropdown:', filteredProviders.length, 'providers found');
  console.log('📋 Provider list:', filteredProviders.map(provider => ({
    name: provider.name,
    courseCount: provider.courses.length,
    languages: [...new Set(provider.courses.flatMap(course => course.languages || []))],
    trainingTypes: [...new Set(provider.courses.flatMap(course => 
      course.trainingCategories ? course.trainingCategories.map(cat => cat.name) : []
    ))]
  })));

  const filteredCountries = useMemo(() => {
    return countries.filter(country => {
      if (!country) return false;
      return country.toLowerCase().includes(countrySearch.toLowerCase());
    });
  }, [countries, countrySearch]);
  
  const filteredLanguages = useMemo(() => {
    console.log('filteredLanguages recalculating with:', { languages, languageSearch });
    const filtered = languages.filter(language =>
    language.toLowerCase().includes(languageSearch.toLowerCase())
    );
    console.log('filteredLanguages result:', filtered);
    return filtered;
  }, [languages, languageSearch]);
  
  const filteredCourses = useMemo(() => {
    console.log('filteredCourses recalculating with:', { courses, courseSearch });
    const filtered = courses.filter(course =>
      course.toLowerCase().includes(courseSearch.toLowerCase())
    );
    console.log('filteredCourses result:', filtered);
    return filtered;
  }, [courses, courseSearch]);

  const filteredCourseObjects = useMemo(() => {
    console.log('filteredCourseObjects recalculating with:', { courseObjects, courseSearch });
    const filtered = courseObjects.filter(course =>
      course.name.toLowerCase().includes(courseSearch.toLowerCase())
    );
    console.log('filteredCourseObjects result:', filtered);
    return filtered;
  }, [courseObjects, courseSearch]);

  const filteredBoards = boards.filter(board =>
    board.toLowerCase().includes(boardSearch.toLowerCase())
  )
  
  const filteredTrainingCategories = useMemo(() => {
    return trainingCategories.filter(category => {
      const apiValue = category.toLowerCase();
      const searchTerm = trainingCategorySearch.toLowerCase();
      
      // Create friendly label inline to avoid reference error
      const labelMap = {
        'online': 'Instructor led - Online',
        'onsite': 'Instructor led - Onsite',
        'elearning': 'eLearning'
      };
      const friendlyLabel = (labelMap[category] || category).toLowerCase();
      
      // Match against both API value and friendly label
      return apiValue.includes(searchTerm) || friendlyLabel.includes(searchTerm);
    });
  }, [trainingCategories, trainingCategorySearch]);

  const filteredMaterialTypes = useMemo(() => {
    return materialTypes.filter(type => {
      const apiValue = type.toLowerCase();
      const searchTerm = materialTypeSearch.toLowerCase();
      
      // Create friendly label inline to avoid reference error
      const labelMap = {
        'TP': 'Training Provider(TP)',
        'TMO': 'Training Material Owner(TMO)', 
        'TMO&TP': 'Training Material Owner and Training Provider(TMO&TP)'
      };
      const friendlyLabel = (labelMap[type] || type).toLowerCase();
      
      // Match against both API value and friendly label
      return apiValue.includes(searchTerm) || friendlyLabel.includes(searchTerm);
    });
  }, [materialTypes, materialTypeSearch]);

  const closeAllDropdowns = () => {
    setIsOpen(false)
    setCountryOpen(false)
    setLanguageOpen(false)
    setCourseOpen(false)
    setBoardOpen(false)
    setTrainingCategoryOpen(false)
    setMaterialTypeOpen(false)
  }

  const countryRef = useRef(null)
  const languageRef = useRef(null)
  const courseRef = useRef(null)
  const boardRef = useRef(null)
  const trainingCategoryRef = useRef(null)
  const materialTypeRef = useRef(null)
  const providerSearchRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {

      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setCountryOpen(false)
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageOpen(false)
      }
      if (courseRef.current && !courseRef.current.contains(event.target)) {
        setCourseOpen(false)
      }
      if (boardRef.current && !boardRef.current.contains(event.target)) {
        setBoardOpen(false)
      }
      if (trainingCategoryRef.current && !trainingCategoryRef.current.contains(event.target)) {
        setTrainingCategoryOpen(false)
      }
      if (materialTypeRef.current && !materialTypeRef.current.contains(event.target)) {
        setMaterialTypeOpen(false)
      }
      if (providerSearchRef.current && !providerSearchRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const fetchSoapData = async () => {
    try {
      // More robust API URL generation
      let apiUrl;
      if (window.wpApiSettings?.root) {
        apiUrl = `${window.wpApiSettings.root}istqb/v1/training-providers`;
      } else {
        // Fallback URL construction
        const baseUrl = window.location.origin;
        apiUrl = `${baseUrl}/wp-json/istqb/v1/training-providers`;
      }
      
      console.log('🔄 Initiating data fetch from:', apiUrl);
      console.log('wpApiSettings available:', !!window.wpApiSettings);

      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add nonce if available
      if (window.wpApiSettings?.nonce) {
        headers['X-WP-Nonce'] = window.wpApiSettings.nonce;
      }

      const response = await fetch(apiUrl, {
        credentials: 'same-origin',
        headers
      });

      if (!response.ok) {
        console.error('❌ API Error:', response.status, response.statusText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check for cache status header and show appropriate message
      const cacheStatus = response.headers.get('X-Cache-Status');
      if (cacheStatus) {
        if (cacheStatus.includes('MISS')) {
          console.log('🌐 Fresh data fetched from API');
          console.log(`📝 Cache Status: ${cacheStatus}`);
        } else if (cacheStatus.includes('HIT')) {
          console.log('💾 Using cached data');
          console.log(`📝 Cache Status: ${cacheStatus}`);
          const match = cacheStatus.match(/Age: (\d+)s/);
          if (match) {
            console.log(`⏱️ Cache age: ${match[1]} seconds`);
          }
        }
      }

      const data = await response.text();
      
      if (!data || data.trim() === '') {
        console.error('❌ Empty response received');
        throw new Error('Empty response received');
      }

      return data;

    } catch (error) {
      console.error('❌ Error in fetchSoapData:', error);
      return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      let xmlData;
      
      console.log('🔄 Starting data load, useApi:', useApi);
      
      try {
        if (useApi) {
          xmlData = await fetchSoapData();
          if (!xmlData) {
            console.log('🔄 API failed, switching to local XML');
            setUseApi(false);
            return;
          }
        } 
        
        if (!useApi) {
          try {
            const xmlUrl = `${props.pluginUrl}/public/training.xml`;
            console.log('🔄 Loading local XML from:', xmlUrl);
            const response = await fetch(xmlUrl);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            xmlData = await response.text();
            console.log('✅ Successfully loaded local XML, length:', xmlData.length);
          } catch (error) {
            console.error('❌ Error loading XML:', error);
          }
        }

        if (xmlData) {
          console.log('🔄 Parsing XML data...');
          const parsedData = parseXMLData(xmlData);
          console.log('✅ Parsed XML data:', { 
            providersCount: parsedData.providers.length,
            sampleProvider: parsedData.providers[0]
          });
          
          // Store both the parsed data and raw XML data for dev panel
          setBoardData(parsedData);
          setRawXmlData(parsedData);
          setProviders(parsedData.providers);
          setIsInitialLoading(false);
          
          // Don't show any results initially - user must click search button
          setSearchResults([]);
          
          // Extract unique courses from all providers with full course objects
          const allCourseObjects = parsedData.providers
            .flatMap(provider => provider.courses
              .filter(course => 
                course.name && 
                typeof course.name === 'string' &&
                course.languages && 
                course.languages.length > 0  // Only include courses with languages
              )
            );
          
          // Remove duplicates based on course name
          const uniqueCourseObjects = allCourseObjects.filter((course, index, self) => 
            index === self.findIndex(c => c.name.trim() === course.name.trim())
          ).sort((a, b) => a.name.localeCompare(b.name));
          
          // Extract course names for backward compatibility
          const allCourses = uniqueCourseObjects.map(course => course.name.trim());
          
          console.log('✅ Extracted course objects:', uniqueCourseObjects);
          console.log('✅ Extracted course names:', allCourses);
          console.log('🔍 Sample courses with languages:', parsedData.providers
            .slice(0, 3)
            .flatMap(provider => provider.courses)
            .filter(course => course.languages && course.languages.length > 0)
          );
          setCourses(allCourses);
          
          // Store course objects for hierarchical grouping
          setCourseObjects(uniqueCourseObjects);
          
          // Extract unique countries from training categories and accredited boards
          const uniqueCountries = [...new Set([
            // Countries from training categories
            ...parsedData.providers
              .flatMap(provider => provider.courses)
              .flatMap(course => course.trainingCategories || [])
              .map(category => category.countriesOfOperation || '')
              .filter(countries => countries && countries.trim() !== '')
              .flatMap(countries => countries.split(',').map(country => country.trim())),
            // Countries from accredited boards  
            ...parsedData.providers
              .flatMap(provider => provider.courses)
              .map(course => course.accreditatedBoard?.countriesOfOperation || '')
              .filter(countries => countries && countries.trim() !== '')
              .flatMap(countries => countries.split(',').map(country => country.trim()))
          ])].filter(country => country && country.trim() !== '');
          console.log('✅ Extracted countries from courses:', uniqueCountries);
          setCountries(uniqueCountries.sort());

          const uniqueLanguages = [...new Set(parsedData.providers
            .flatMap(provider => provider.courses)
            .flatMap(course => course.languages)
            .filter(lang => lang && lang.trim() !== ''))];
          console.log('Extracted languages:', uniqueLanguages);
          console.log('🔍 Sample language extraction:', parsedData.providers
            .slice(0, 3)
            .flatMap(provider => provider.courses)
            .map(course => ({ courseName: course.name, languages: course.languages }))
          );
          setLanguages(uniqueLanguages.sort());
          
          const uniqueTrainingCategories = [...new Set(parsedData.providers
            .flatMap(provider => provider.courses)
            .flatMap(course => course.trainingCategories)
            .map(category => category.name)
            .filter(categoryName => categoryName && categoryName.trim() !== ''))];
          console.log('Extracted training categories:', uniqueTrainingCategories);
          setTrainingCategories(uniqueTrainingCategories.sort());
          
          const uniqueMaterialTypes = [...new Set(parsedData.providers
            .flatMap(provider => provider.courses)
            .map(course => course.type)
            .filter(type => type && type.trim() !== ''))];
          console.log('Extracted material types:', uniqueMaterialTypes);
          setMaterialTypes(uniqueMaterialTypes.sort());
          
          // Debug board extraction
          console.log('🔍 Debugging board extraction:');
          console.log('🔍 First 3 providers accredited boards:', parsedData.providers.slice(0, 3).map(provider => ({
            providerName: provider.name,
            accreditatedBoard: provider.accreditatedBoard,
            nameBoard: provider.accreditatedBoard?.nameBoard
          })));
          
          // Extract boards from both provider level and course level
          const providerBoards = parsedData.providers
            .map(provider => provider.accreditatedBoard?.nameBoard)
            .filter(board => board && board.trim() !== '');
            
          const courseBoards = parsedData.providers
            .flatMap(provider => provider.courses)
            .map(course => course.accreditatedBoard?.nameBoard)
            .filter(board => board && board.trim() !== '');
            
          console.log('🔍 Provider level boards:', providerBoards);
          console.log('🔍 Course level boards:', courseBoards);
          
          const uniqueBoards = [...new Set([...providerBoards, ...courseBoards])]
            .sort((a, b) => a.localeCompare(b));
          console.log('🔍 Final extracted boards:', uniqueBoards);
          setBoards(uniqueBoards);
          
          console.log('Final state - courses:', allCourses, 'languages:', uniqueLanguages, 'trainingCategories:', uniqueTrainingCategories, 'providers:', parsedData.providers.length);
        }
      } catch (error) {
        console.error('Error:', error);
        setIsInitialLoading(false);
      }
    };

    loadData();
  }, [useApi, props.pluginUrl]);

  useEffect(() => {
    if (!providers.length || !isSearchLoading) return;
    
    const results = providers.filter(provider => {
      const matchesProvider = !searchTerm || 
        provider.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCountry = !countrySearch || provider.courses.some(course => {
        // Check training category countries of operation
        const trainingCategoryMatch = course.trainingCategories && course.trainingCategories.some(category => {
          // If searching for a specific training type, only check that type's countries
          if (trainingCategorySearch && trainingCategorySearch.trim() !== '') {
            const matchesTrainingType = category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase());
            if (!matchesTrainingType) return false; // Skip categories that don't match the search
            console.log(`🎯 Checking only "${trainingCategorySearch}" training type countries for ${provider.name}`);
          }
          
          const countriesOfOperation = category.countriesOfOperation || '';
          const match = countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
          if (match && countrySearch) {
            console.log(`🌍 Training category match for "${countrySearch}": ${category.name} operates in ${countriesOfOperation}`);
            console.log(`🎯 Primary group: ${provider.name} - Training category "${category.name}" operates in ${countriesOfOperation}`);
          }
          return match;
        });
        
        // Check accredited board countries of operation (only if no specific training type is selected)
        let accreditedBoardMatch = false;
        if (!trainingCategorySearch || trainingCategorySearch.trim() === '') {
          accreditedBoardMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
            course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
          if (accreditedBoardMatch && countrySearch) {
            console.log(`🏛️ Accredited board match for "${countrySearch}": ${course.accreditatedBoard.nameBoard} operates in ${course.accreditatedBoard.countriesOfOperation}`);
          }
        }
        
        return trainingCategoryMatch || accreditedBoardMatch;
      });
      
      // New integrated filtering approach: check if provider has courses matching ALL specified criteria including board
      const hasAnyCourseFilters = courseSearch || materialTypeSearch || languageSearch || trainingCategorySearch || boardSearch;
      
      let matchesCourse = true;
      let matchesLanguage = true;
      let matchesTrainingCategory = true;
      let matchesMaterialType = true;
      let matchesBoard = true;
      
      if (hasAnyCourseFilters) {
        // When any course-related filters are applied, find courses that match ALL criteria
        const matchesAllCriteria = provider.courses.some(course => {
        // Skip courses without languages
        if (!course.languages || course.languages.length === 0) {
          return false;
        }

        if (!course.name || typeof course.name !== 'string') {
          return false;
        }
        
          // Check course name match
        const courseName = course.name.trim();
          const courseNameMatches = !courseSearch || courseName.toLowerCase() === courseSearch.trim().toLowerCase();
          
          // Check material type match
          const materialTypeMatches = !materialTypeSearch || course.type === materialTypeSearch;
          
          // Check language match
          const languageMatches = !languageSearch || 
            course.languages.some(lang => lang.toLowerCase().includes(languageSearch.toLowerCase()));
          
          // Check training category match
          const trainingCategoryMatches = !trainingCategorySearch || 
            (course.trainingCategories && course.trainingCategories.some(category =>
              category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase())
            ));
          
          // Check board match - this course must be accredited by the specified board
          const boardMatches = !boardSearch || 
            course.accreditatedBoard?.nameBoard?.toLowerCase().includes(boardSearch.toLowerCase());
          
          // Course must match ALL specified criteria including board
          let courseMatchesAll = courseNameMatches && materialTypeMatches && languageMatches && trainingCategoryMatches && boardMatches;
          
          // If course matches all criteria and we're also filtering by country, check local accreditation for this specific course
          if (courseMatchesAll && countrySearch && countrySearch.trim() !== '') {
          // Check training category countries of operation for this specific course
            const trainingCategoryCountryMatch = course.trainingCategories && course.trainingCategories.some(category => {
            // If searching for a specific training type, only check that type's countries
            if (trainingCategorySearch && trainingCategorySearch.trim() !== '') {
              const matchesTrainingType = category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase());
              if (!matchesTrainingType) return false; // Skip categories that don't match the search
              console.log(`🎯 Checking only "${trainingCategorySearch}" training type countries for specific course "${courseName}" in ${provider.name}`);
            }
            
            const countriesOfOperation = category.countriesOfOperation || '';
            const match = countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
            if (match && countrySearch) {
              console.log(`🌍 Course-specific training category match for "${countrySearch}": Course "${courseName}" in ${provider.name} - Training category "${category.name}" operates in ${countriesOfOperation}`);
            }
            return match;
          });
          
          // Check accredited board countries of operation for this specific course (only if no specific training type is selected)
            let accreditedBoardCountryMatch = false;
          if (!trainingCategorySearch || trainingCategorySearch.trim() === '') {
              accreditedBoardCountryMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
              course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
              if (accreditedBoardCountryMatch && countrySearch) {
              console.log(`🏛️ Course-specific accredited board match for "${countrySearch}": Course "${courseName}" in ${provider.name} - Accredited by "${course.accreditatedBoard.nameBoard}" operating in ${course.accreditatedBoard.countriesOfOperation}`);
            }
          }
          
            // Course must match all criteria AND have local accreditation
            return courseMatchesAll && (trainingCategoryCountryMatch || accreditedBoardCountryMatch);
          }
          
          return courseMatchesAll;
        });
        
        matchesCourse = matchesAllCriteria;
        matchesLanguage = matchesAllCriteria;
        matchesTrainingCategory = matchesAllCriteria;
        matchesMaterialType = matchesAllCriteria;
        matchesBoard = matchesAllCriteria;
      }
      
      return matchesProvider && matchesCountry && matchesCourse && matchesLanguage && matchesTrainingCategory && matchesMaterialType && matchesBoard;
    });
    
    // Group results by accreditation board location when filtering by country
    let sortedResults;
    if (countrySearch && countrySearch.trim() !== '') {
      console.log('🔍 Grouping results by countries of operation and board accreditation for:', countrySearch, 
        trainingCategorySearch ? `(training type: ${trainingCategorySearch})` : '(all training types)');
      
      const primaryGroup = []; // Providers with local accreditation
      const secondaryGroup = []; // Providers without local accreditation
      
      results.forEach(provider => {
        // Use the same logic as the hasLocalAccreditation function for consistency
        const providerHasLocalAccreditation = hasLocalAccreditation(provider, countrySearch, courseSearch);
        
        if (providerHasLocalAccreditation) {
          primaryGroup.push(provider);
          console.log(`🎯 Primary group: ${provider.name} - Has local accreditation for "${countrySearch}"`);
        } else {
          secondaryGroup.push(provider);
          console.log(`🔄 Secondary group: ${provider.name} - No local accreditation for "${countrySearch}"`);
        }
      });
      
      console.log('🔍 Primary group (local accreditation):', primaryGroup.length);
      console.log('🔍 Secondary group (no local accreditation):', secondaryGroup.length);
      
      // Shuffle each group separately, then combine
      const shuffledPrimary = shuffleArray(primaryGroup);
      const shuffledSecondary = shuffleArray(secondaryGroup);
      
      sortedResults = [...shuffledPrimary, ...shuffledSecondary];
    } else {
      // No country filter, just shuffle normally
      sortedResults = shuffleArray(results);
    }
    
    setSearchResults(sortedResults);
    setIsSearchLoading(false);
  }, [isSearchLoading, providers, searchTerm, countrySearch, boardSearch, courseSearch, languageSearch, trainingCategorySearch, materialTypeSearch]);

  const clearAllSearches = () => {
    setSearchTerm("")
    setCountrySearch("")
    setLanguageSearch("")
    setCourseSearch("")
    setBoardSearch("")
    setTrainingCategorySearch("")
    setMaterialTypeSearch("")
    closeAllDropdowns()
  }

  // Helper function to get accreditation type explanation
  const getMaterialTypeLabel = (type) => {
    // First format the type to ensure proper spacing
    const formattedType = type === 'TMO&TP' ? 'TMO & TP' : type;
    
    const explanations = {
      'TP': 'Training Provider ',
      'TMO': 'Training Material Owner ', 
      'TMO&TP': 'Training Material Owner and Training Provider '
    };
    const label = explanations[type] || formattedType;
    const abbr = type === 'TMO&TP' ? '(TMO & TP)' : `(${type})`;
    return <>{label}<span className="text-[14px]">{abbr}</span></>;
  };

  // Helper function to get the display value for the input field
  const getMaterialTypeDisplayValue = (searchValue) => {
    if (!searchValue) return '';
    // If the search value exactly matches an API value, show the friendly label
    const explanations = {
      'TP': 'Training Provider (TP)',
      'TMO': 'Training Material Owner (TMO)', 
      'TMO&TP': 'Training Material Owner and Training Provider (TMO & TP)'
    };
    return explanations[searchValue] || (searchValue === 'TMO&TP' ? 'TMO & TP' : searchValue);
  };

  // Helper function to get training type explanation
  const getTrainingTypeLabel = (type) => {
    const explanations = {
      'online': 'Instructor led - Online',
      'onsite': 'Instructor led - Onsite',
      'elearning': 'eLearning'
    };
    return explanations[type] || type;
  };

  // Helper function to get the display value for training type input field
  const getTrainingTypeDisplayValue = (searchValue) => {
    if (!searchValue) return '';
    // If the search value exactly matches an API value, show the friendly label
    const explanations = {
      'online': 'Instructor led - Online',
      'onsite': 'Instructor led - Onsite',
      'elearning': 'eLearning'
    };
    return explanations[searchValue] || searchValue;
  };

  // Helper function to check if provider has local accreditation for the searched country
  const hasLocalAccreditation = (provider, searchedCountry, specificCourse = null) => {
    if (!searchedCountry || !searchedCountry.trim()) return false;
    
    // If searching for a specific course, only check that course's accreditation
    if (specificCourse && specificCourse.trim() !== '') {
    return provider.courses.some(course => {
        // First check if this is the specific course we're looking for
        const isMatchingCourse = course.name && 
          course.name.toLowerCase().trim() === specificCourse.toLowerCase().trim();
        
        if (!isMatchingCourse) return false; // Skip courses that don't match the search
        
        // Local accreditation should ONLY be based on accredited board countries, not training delivery locations
        // Check accredited board countries of operation for this specific course
        const accreditedBoardMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
          course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(searchedCountry.toLowerCase());
        if (accreditedBoardMatch && searchedCountry) {
          console.log(`🎯 ACCREDITED BOARD LOCAL ACCREDITATION: Course "${specificCourse}" in ${provider.name} shows local accreditation because accredited board "${course.accreditatedBoard.nameBoard}" operates in "${course.accreditatedBoard.countriesOfOperation}" (searched for "${searchedCountry}")`);
        }
        
        return accreditedBoardMatch;
      });
    }
    
    // If not searching for a specific course, check if ANY course has local accreditation
    return provider.courses.some(course => {
      // Local accreditation should ONLY be based on accredited board countries, not training delivery locations
      // Check accredited board countries of operation
      const accreditedBoardMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
          course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(searchedCountry.toLowerCase());
        if (accreditedBoardMatch && searchedCountry) {
        console.log(`🏛️ ACCREDITED BOARD LOCAL ACCREDITATION: ${provider.name} shows local accreditation because course "${course.name}" is accredited by "${course.accreditatedBoard.nameBoard}" which operates in "${course.accreditatedBoard.countriesOfOperation}" (searched for "${searchedCountry}")`);
      }
      
      return accreditedBoardMatch;
    });
  };

  const handleSearch = () => {
    if (!providers.length) {
      setIsSearchLoading(true);
      return;
    }
    
    const results = providers.filter(provider => {
      const matchesProvider = !searchTerm || 
        provider.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCountry = !countrySearch || provider.courses.some(course => {
        // Check training category countries of operation
        const trainingCategoryMatch = course.trainingCategories && course.trainingCategories.some(category => {
          // If searching for a specific training type, only check that type's countries
          if (trainingCategorySearch && trainingCategorySearch.trim() !== '') {
            const matchesTrainingType = category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase());
            if (!matchesTrainingType) return false; // Skip categories that don't match the search
            console.log(`🎯 Checking only "${trainingCategorySearch}" training type countries for ${provider.name}`);
          }
          
          const countriesOfOperation = category.countriesOfOperation || '';
          const match = countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
          if (match && countrySearch) {
            console.log(`🌍 Training category match for "${countrySearch}": ${category.name} operates in ${countriesOfOperation}`);
            console.log(`🎯 Primary group: ${provider.name} - Training category "${category.name}" operates in ${countriesOfOperation}`);
          }
          return match;
        });
        
        // Check accredited board countries of operation (only if no specific training type is selected)
        let accreditedBoardMatch = false;
        if (!trainingCategorySearch || trainingCategorySearch.trim() === '') {
          accreditedBoardMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
            course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
        }
        
        return trainingCategoryMatch || accreditedBoardMatch;
      });
      
      // New integrated filtering approach: check if provider has courses matching ALL specified criteria including board
      const hasAnyCourseFilters = courseSearch || materialTypeSearch || languageSearch || trainingCategorySearch || boardSearch;
      
      let matchesCourse = true;
      let matchesLanguage = true;
      let matchesTrainingCategory = true;
      let matchesMaterialType = true;
      let matchesBoard = true;
      
      if (hasAnyCourseFilters) {
        // When any course-related filters are applied, find courses that match ALL criteria
        const matchesAllCriteria = provider.courses.some(course => {
        // Skip courses without languages
        if (!course.languages || course.languages.length === 0) {
          return false;
        }

        if (!course.name || typeof course.name !== 'string') {
          return false;
        }
        
          // Check course name match
        const courseName = course.name.trim();
          const courseNameMatches = !courseSearch || courseName.toLowerCase() === courseSearch.trim().toLowerCase();
          
          // Check material type match
          const materialTypeMatches = !materialTypeSearch || course.type === materialTypeSearch;
          
          // Check language match
          const languageMatches = !languageSearch || 
            course.languages.some(lang => lang.toLowerCase().includes(languageSearch.toLowerCase()));
          
          // Check training category match
          const trainingCategoryMatches = !trainingCategorySearch || 
            (course.trainingCategories && course.trainingCategories.some(category =>
              category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase())
            ));
          
          // Check board match - this course must be accredited by the specified board
          const boardMatches = !boardSearch || 
            course.accreditatedBoard?.nameBoard?.toLowerCase().includes(boardSearch.toLowerCase());
          
          // Course must match ALL specified criteria including board
          let courseMatchesAll = courseNameMatches && materialTypeMatches && languageMatches && trainingCategoryMatches && boardMatches;
          
          // If course matches all criteria and we're also filtering by country, check local accreditation for this specific course
          if (courseMatchesAll && countrySearch && countrySearch.trim() !== '') {
          // Check training category countries of operation for this specific course
            const trainingCategoryCountryMatch = course.trainingCategories && course.trainingCategories.some(category => {
            // If searching for a specific training type, only check that type's countries
            if (trainingCategorySearch && trainingCategorySearch.trim() !== '') {
              const matchesTrainingType = category.name.toLowerCase().includes(trainingCategorySearch.toLowerCase());
              if (!matchesTrainingType) return false; // Skip categories that don't match the search
              console.log(`🎯 Checking only "${trainingCategorySearch}" training type countries for specific course "${courseName}" in ${provider.name}`);
            }
            
            const countriesOfOperation = category.countriesOfOperation || '';
            const match = countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
            if (match && countrySearch) {
              console.log(`🌍 Course-specific training category match for "${countrySearch}": Course "${courseName}" in ${provider.name} - Training category "${category.name}" operates in ${countriesOfOperation}`);
            }
            return match;
          });
          
          // Check accredited board countries of operation for this specific course (only if no specific training type is selected)
            let accreditedBoardCountryMatch = false;
          if (!trainingCategorySearch || trainingCategorySearch.trim() === '') {
              accreditedBoardCountryMatch = course.accreditatedBoard && course.accreditatedBoard.countriesOfOperation && 
              course.accreditatedBoard.countriesOfOperation.toLowerCase().includes(countrySearch.toLowerCase());
              if (accreditedBoardCountryMatch && countrySearch) {
              console.log(`🏛️ Course-specific accredited board match for "${countrySearch}": Course "${courseName}" in ${provider.name} - Accredited by "${course.accreditatedBoard.nameBoard}" operating in ${course.accreditatedBoard.countriesOfOperation}`);
            }
          }
          
            // Course must match all criteria AND have local accreditation
            return courseMatchesAll && (trainingCategoryCountryMatch || accreditedBoardCountryMatch);
          }
          
          return courseMatchesAll;
        });
        
        matchesCourse = matchesAllCriteria;
        matchesLanguage = matchesAllCriteria;
        matchesTrainingCategory = matchesAllCriteria;
        matchesMaterialType = matchesAllCriteria;
        matchesBoard = matchesAllCriteria;
      }
      
      return matchesProvider && matchesCountry && matchesCourse && matchesLanguage && matchesTrainingCategory && matchesMaterialType && matchesBoard;
    });
    
    // Group results by local accreditation when filtering by country
    let sortedResults;
    if (countrySearch && countrySearch.trim() !== '') {
      console.log('🔍 Grouping results by local accreditation for:', countrySearch, 
        courseSearch ? `(course: ${courseSearch})` : '(all courses)');
      
      const primaryGroup = []; // Providers with local accreditation
      const secondaryGroup = []; // Providers without local accreditation
      
      results.forEach(provider => {
        // Use the same logic as the hasLocalAccreditation function for consistency
        const providerHasLocalAccreditation = hasLocalAccreditation(provider, countrySearch, courseSearch);
        
        if (providerHasLocalAccreditation) {
          primaryGroup.push(provider);
          console.log(`🎯 Primary group: ${provider.name} - Has local accreditation for "${countrySearch}"`);
        } else {
          secondaryGroup.push(provider);
          console.log(`🔄 Secondary group: ${provider.name} - No local accreditation for "${countrySearch}"`);
        }
      });
      
      console.log('🔍 Primary group (local accreditation):', primaryGroup.length);
      console.log('🔍 Secondary group (no local accreditation):', secondaryGroup.length);
      
      // Shuffle each group separately, then combine
      const shuffledPrimary = shuffleArray(primaryGroup);
      const shuffledSecondary = shuffleArray(secondaryGroup);
      
      sortedResults = [...shuffledPrimary, ...shuffledSecondary];
    } else {
      // No country filter, just shuffle normally
      sortedResults = shuffleArray(results);
    }
    
    setSearchResults(sortedResults);
    setIsSearchLoading(false);
    closeAllDropdowns();
  };

  // Helper function to merge duplicate courses with same ID and name
  const mergeDuplicateCourses = (courses) => {
    const courseMap = new Map();
    
    courses.forEach(course => {
      // Use both ID and name as the unique key
      const key = `${course.id}-${course.name}`;
      
      if (courseMap.has(key)) {
        // Merge with existing course
        const existingCourse = courseMap.get(key);
        
        // Merge languages (remove duplicates)
        const mergedLanguages = [...new Set([...existingCourse.languages, ...course.languages])];
        
        // Merge training categories (remove duplicates)
        const mergedTrainingCategories = [...new Set([
          ...existingCourse.trainingCategories.map(cat => cat.name),
          ...course.trainingCategories.map(cat => cat.name)
        ])].map(name => {
          // Find the full category object for each unique name
          return existingCourse.trainingCategories.find(cat => cat.name === name) ||
                 course.trainingCategories.find(cat => cat.name === name);
        });
        
        // Merge accredited boards
        const mergedAccreditatedBoards = [];
        if (existingCourse.accreditatedBoard?.nameBoard) {
          mergedAccreditatedBoards.push(existingCourse.accreditatedBoard);
        }
        if (course.accreditatedBoard?.nameBoard && 
            course.accreditatedBoard.nameBoard !== existingCourse.accreditatedBoard?.nameBoard) {
          mergedAccreditatedBoards.push(course.accreditatedBoard);
        }
        
        // Update the existing course with merged data
        courseMap.set(key, {
          ...existingCourse,
          languages: mergedLanguages,
          trainingCategories: mergedTrainingCategories,
          accreditatedBoards: mergedAccreditatedBoards, // New array field for multiple boards
          accreditatedBoard: existingCourse.accreditatedBoard // Keep original for compatibility
        });
      } else {
        // First occurrence of this course
        courseMap.set(key, {
          ...course,
          accreditatedBoards: course.accreditatedBoard?.nameBoard ? [course.accreditatedBoard] : []
        });
      }
    });
    
    return Array.from(courseMap.values());
  };

  // In the modal, display certification information
  const renderCertifications = (provider) => {
    // Filter courses to only include those with languages
    const coursesWithLanguages = provider.courses.filter(course => 
      course.languages && course.languages.length > 0
    );

    // Merge duplicate courses (same ID and name but different accredited boards)
    const mergedCourses = mergeDuplicateCourses(coursesWithLanguages);
    
    // Always show ALL courses in the modal - no filtering based on search criteria
    const filteredCourses = mergedCourses;

    if (filteredCourses.length === 0) {
      return null;
    }

    // Group courses using the same logic as the dropdown
    const groupedCourses = groupCoursesForDropdown(filteredCourses);

    if (Object.keys(groupedCourses).length === 0) {
    return (
      <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900">Available Courses</h4>
          <div className="mt-2">
          <ul className="space-y-2">
              {filteredCourses.sort(sortCoursesByAcronymSuffix).map((course, index) => {
                // Clean up course name by removing redundant prefixes and subheadings
                const cleanCourseName = (name) => {
                  let cleanName = name;
                  
                  // Remove common prefixes and subheadings
                  const prefixesToRemove = [
                    'Core Foundation ',
                    'Core Advanced ',
                    'Specialist Technologies & Approaches ',
                    'Specialist Quality Characteristics & Test Levels ',
                    'Specialist Testing in Particular Domains ',
                    'Expert Test Management ',
                    'Expert Improving the Testing Process ',  // With "the"
                    'Expert Improving Testing Process ',      // Without "the"
                    'Certified Tester ',
                    'Advanced Level ',
                    'Foundation Level ',
                    'ISTQB Certified Tester ',
                    'ISTQB ',
                    'Core ',
                    'Specialist ',
                    'Expert ',
                    'Advanced ',
                    'Foundation ',
                    'Test ',
                    'Testing ',
                    '- '
                  ];
                  
                  // Apply prefix removal iteratively (some names might have multiple prefixes)
                  let previousLength;
                  do {
                    previousLength = cleanName.length;
                    prefixesToRemove.forEach(prefix => {
                      if (cleanName.startsWith(prefix)) {
                        cleanName = cleanName.substring(prefix.length);
                      }
                    });
                  } while (cleanName.length < previousLength && cleanName.length > 0);
                  
                  // Clean up any double spaces and trim
                  cleanName = cleanName.replace(/\s+/g, ' ').trim();
                  
                  // If the name becomes too short or empty, use original name
                  if (cleanName.length < 3) {
                    cleanName = name;
                  }
                  
                  return cleanName;
                };

                return (
                <li key={index} className="text-sm text-gray-600 py-1">
                  • {cleanCourseName(course.name)}
                  <div className="text-xs text-gray-500 mt-1 ml-3 space-y-2">
                    <div>Languages: {course.languages.join(", ")}</div>
                    {course.acronym && (
                      <div>Acronym: {course.acronym}</div>
                    )}
                    {course.type && (
                      <div>Accreditation Type: {getMaterialTypeLabel(course.type)}</div>
                    )}
                {course.trainingCategories && course.trainingCategories.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span>Training Type:</span>
                        {course.trainingCategories.map((cat, catIndex) => (
                          <span 
                            key={catIndex} 
                            className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full"
                          >
                            {getTrainingTypeLabel(cat.name)}
                  </span>
                        ))}
                      </div>
                )}
                {course.accreditatedBoards && course.accreditatedBoards.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span>Accrediting Boards:</span>
                        {course.accreditatedBoards.map((board, boardIndex) => (
                          <span 
                            key={boardIndex} 
                            className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full"
                          >
                            {board.nameBoard}
                  </span>
                        ))}
                      </div>
                )}
                  </div>
              </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-900">Available Courses</h4>
        <div className={`mt-2 ${mergedCourses.length > 8 ? 'max-h-80 overflow-y-auto' : ''}`}>
          {Object.entries(groupedCourses).map(([type, typeLevels]) => (
            <div key={type} className="mb-4 border-l-4 border-blue-500 pl-3">
              {/* Type Header (e.g., Core, Specialist, Expert) */}
              <div className="font-semibold text-blue-600 text-sm mb-2">{type}</div>
              
              {/* Level Groups (e.g., Foundation, Advanced) */}
              {Object.entries(typeLevels).map(([level, levelCourses]) => (
                <div key={`${type}-${level}`} className="mb-3 ml-2">
                  {/* Level Header */}
                  <div className="font-medium text-gray-700 text-sm mb-1">{level}</div>
                  
                  {/* Individual Courses */}
                  <ul className="ml-2 space-y-2">
                    {levelCourses.map((course, index) => {
                      // Clean up course name by removing redundant prefixes and subheadings
                      const cleanCourseName = (name) => {
                        let cleanName = name;
                        
                        // Remove only section headings and subheadings, not individual words that could be part of course names
                        const prefixesToRemove = [
                          'Core Foundation ',
                          'Core Advanced ',
                          'Specialist Technologies & Approaches ',
                          'Specialist Quality Characteristics & Test Levels ',
                          'Specialist Testing in Particular Domains ',
                          'Expert Test Management ',
                          'Expert Improving the Testing Process ',  // With "the"
                          'Expert Improving Testing Process ',      // Without "the"
                          'Istqb Certified Tester ',
                          'ISTQB Certified Tester ',
                          'Certified Tester ',
                          'Advanced Level ',
                          'Foundation Level ',
                          'ISTQB '
                        ];
                        
                        // Apply prefix removal iteratively (some names might have multiple prefixes)
                        let previousLength;
                        do {
                          previousLength = cleanName.length;
                          prefixesToRemove.forEach(prefix => {
                            if (cleanName.startsWith(prefix)) {
                              cleanName = cleanName.substring(prefix.length);
                            }
                          });
                        } while (cleanName.length < previousLength && cleanName.length > 0);
                        
                        // Clean up any double spaces and trim
                        cleanName = cleanName.replace(/\s+/g, ' ').trim();
                        
                        // If the name becomes too short or empty, use original name
                        if (cleanName.length < 3) {
                          cleanName = name;
                        }
                        
                        return cleanName;
                      };

                      return (
                      <li key={`${type}-${level}-${index}`} className="text-sm text-gray-600">
                        • {cleanCourseName(course.name)}
                        <div className="text-xs text-gray-500 mt-1 ml-3 space-y-2">
                          <div>Languages: {course.languages.join(", ")}</div>
                          {course.acronym && (
                            <div>Acronym: {course.acronym}</div>
                          )}
                          {course.type && (
                            <div>Accreditation Type: {getMaterialTypeLabel(course.type)}</div>
                          )}
                          {course.trainingCategories && course.trainingCategories.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span>Training Type:</span>
                              {course.trainingCategories.map((cat, catIndex) => (
                                <span 
                                  key={catIndex} 
                                  className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full"
                                >
                                  {getTrainingTypeLabel(cat.name)}
                                </span>
                              ))}
                            </div>
                          )}
                          {course.accreditatedBoards && course.accreditatedBoards.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span>Accrediting Boards:</span>
                              {course.accreditatedBoards.map((board, boardIndex) => (
                                <span 
                                  key={boardIndex} 
                                  className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full"
                                >
                                  {board.nameBoard}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                      );
                    })}
          </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Add useEffect to handle body scrolling
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [showModal])

  const isDevelopment = () => {
    return window.location.href.includes('localhost:8888/wordpress/index.php/sample-page/');
  };

  // Shimmer loading component
  const ShimmerLoader = () => (
    <div className="animate-pulse">
      {/* Desktop Layout Shimmer */}
      <div id="desktop-shimmer" style={{ display: isMobileLayout ? 'none' : 'block' }}>
        {/* Shimmer for dropdown filters - 4 columns */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
            <div key={i} className="relative">
              <div className="w-full h-12 bg-gray-200 rounded-lg border"></div>
            </div>
        ))}
      </div>
      
        {/* Second row of filters */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="relative">
              <div className="w-full h-12 bg-gray-200 rounded-lg border"></div>
            </div>
          ))}
        </div>
        
        {/* Search and Clear buttons */}
        <div className="w-full flex items-center mb-6">
          <div className="h-10 w-20 bg-gray-300 rounded-lg mr-4"></div>
          <div className="h-10 w-16 bg-gray-200 rounded-lg"></div>
        </div>
        
        {/* Results table shimmer */}
        <div className="overflow-x-auto">
          <div className="min-w-full border border-gray-200 rounded-lg">
            {/* Table header */}
            <div className="bg-gray-50 border-b border-gray-200">
              <div className="flex">
                <div className="flex-1 px-6 py-3 h-12 bg-gray-100"></div>
                <div className="flex-1 px-6 py-3 h-12 bg-gray-100"></div>
                <div className="w-32 px-6 py-3 h-12 bg-gray-100"></div>
                <div className="w-24 px-6 py-3 h-12 bg-gray-100"></div>
                <div className="w-24 px-6 py-3 h-12 bg-gray-100"></div>
              </div>
            </div>
            
            {/* Table rows */}
            <div className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex hover:bg-gray-50">
                  <div className="flex-1 px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                  <div className="flex-1 px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      <div className="h-6 w-16 bg-blue-100 rounded"></div>
                      <div className="h-6 w-20 bg-blue-100 rounded"></div>
                    </div>
                  </div>
                  <div className="w-32 px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </div>
                  <div className="w-24 px-6 py-4 text-center">
                    <div className="h-6 w-6 bg-gray-200 rounded-full mx-auto"></div>
                  </div>
                  <div className="w-24 px-6 py-4 text-center">
                    <div className="h-8 w-12 bg-gray-300 rounded mx-auto"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout Shimmer */}
      <div id="mobile-shimmer" style={{ display: isMobileLayout ? 'block' : 'none' }}>
        {/* Mobile dropdown filters */}
        <div className="p-6 rounded-lg mb-6">
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="relative">
                <div className="w-full h-12 bg-gray-200 rounded-lg border"></div>
              </div>
            ))}
          </div>
          
          {/* Mobile Search and Clear buttons */}
          <div className="w-full flex items-center mt-6">
            <div className="h-10 w-20 bg-gray-300 rounded-lg mr-4"></div>
            <div className="h-10 w-16 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
        
        {/* Mobile Results cards */}
        <div className="mt-6 space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 w-20 bg-gray-300 rounded mb-2"></div>
              </div>
            </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <div className="h-4 w-4 bg-gray-200 rounded mr-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                </div>
                <div className="flex items-start">
                  <div className="h-4 w-4 bg-gray-200 rounded mr-2 mt-0.5"></div>
                  <div className="flex flex-wrap gap-1">
                    <div className="h-6 w-16 bg-blue-100 rounded"></div>
                    <div className="h-6 w-20 bg-blue-100 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <div className="h-8 w-24 bg-gray-300 rounded"></div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="istqb-training-provider-wrapper" style={{ width: '100vw', maxWidth: '100%', margin: '0', padding: '0 20px' }}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Find any of the many ISTQB® Accredited Training Providers worldwide</h2>
        
        <div className="flex items-center space-x-4">
        {isDevelopment() && (
          <div className="flex items-center">
            <span className="mr-3 text-sm text-gray-600">
              {useApi ? 'Using API' : 'Using Local XML'}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={useApi}
                onChange={() => setUseApi(!useApi)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        )}
          {!isISTQBDomain() && (
            <button
              onClick={() => setShowDevPanel(true)}
              className="flex items-center px-3 py-1 text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg border border-purple-300 transition-colors"
              title="Open XML Data Explorer"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              XML Explorer
            </button>
          )}
        </div>
      </div>
      
      {/* Show shimmer loader while data is loading */}
      {isInitialLoading ? (
        <ShimmerLoader />
      ) : (
        <>
      {/* Desktop Layout */}
      <div id="desktop-dropdowns" style={{ display: isMobileLayout ? 'none' : 'block' }}>
      {/* Filters row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {/* Country Search */}
        <div className="relative" ref={countryRef} style={{ zIndex: countryOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Countries of Operation"
              value={countrySearch}
              onChange={(e) => {
                const value = e.target.value;
                setCountrySearch(value);
                setCountryOpen(true);
              }}
              onFocus={() => {
                setCountryOpen(true);
              }}
            />
            <ClearButton 
              show={countrySearch !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setCountrySearch("");
                setCountryOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                setCountryOpen(!countryOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {countryOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {providers.length === 0 ? (
                <DropdownLoader />
              ) : (
                filteredCountries.map((country, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCountrySearch(country);
                      setCountryOpen(false);
                      setTimeout(() => {
                      }, 0);
                    }}
                  >
                    {country}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Language Search */}
        <div className="relative" ref={languageRef} style={{ zIndex: languageOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Language..."
              value={languageSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLanguageSearch(value);
                setLanguageOpen(true);
              }}
              onFocus={() => {
                setLanguageOpen(true);
              }}
            />
            <ClearButton 
              show={languageSearch !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setLanguageSearch("");
                setLanguageOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                setLanguageOpen(!languageOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${languageOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {languageOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {providers.length === 0 ? (
                <DropdownLoader />
              ) : (
                filteredLanguages.map((language, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLanguageSearch(language);
                      setLanguageOpen(false);
                      setTimeout(() => {
                      }, 0);
                    }}
                  >
                    {language}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Course Search */}
        <div className="relative" ref={courseRef} style={{ zIndex: courseOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Course..."
              value={courseSearch}
              onChange={(e) => {
                const value = e.target.value;
                setCourseSearch(value);
                setCourseOpen(true);
              }}
              onFocus={() => {
                setCourseOpen(true);
              }}
            />
            <ClearButton 
              show={courseSearch !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setCourseSearch("");
                setCourseOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                setCourseOpen(!courseOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${courseOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {courseOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {providers.length === 0 ? (
                <DropdownLoader />
              ) : (() => {
                const groupedCourses = groupCoursesForDropdown(filteredCourseObjects);
                
                // If no groups, show simple list fallback
                if (Object.keys(groupedCourses).length === 0) {
                  return filteredCourseObjects.sort((a, b) => {
                    // Clean course names for proper sorting
                    const cleanName = (name) => {
                      let cleanName = name;
                      const prefixesToRemove = [
                        'Core Foundation ', 'Core Advanced ', 'Specialist Technologies & Approaches ',
                        'Specialist Quality Characteristics & Test Levels ', 'Specialist Testing in Particular Domains ',
                        'Expert Test Management ', 'Expert Improving Testing Process ', 'Istqb Certified Tester ',
                        'ISTQB Certified Tester ', 'Certified Tester ', 'Advanced Level ', 'Foundation Level ', 'ISTQB '
                      ];
                      let previousLength;
                      do {
                        previousLength = cleanName.length;
                        prefixesToRemove.forEach(prefix => {
                          if (cleanName.startsWith(prefix)) {
                            cleanName = cleanName.substring(prefix.length);
                          }
                        });
                      } while (cleanName.length < previousLength && cleanName.length > 0);
                      cleanName = cleanName.replace(/\s+/g, ' ').trim();
                      return cleanName.length < 3 ? name : cleanName;
                    };
                    return cleanName(a.name).localeCompare(cleanName(b.name));
                  }).map((course, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const selectedCourse = course.name.trim();
                      setCourseSearch(selectedCourse);
                      setCourseOpen(false);
                    }}
                  >
                    {course.name}
                    {course.acronym && (
                      <span className="text-gray-500 text-sm ml-2">
                        ({course.acronym})
                      </span>
                    )}
                  </div>
                  ));
                }
                
                return Object.entries(groupedCourses).map(([type, typeLevels]) => (
                  <div key={type}>
                    {/* Type Header (e.g., Core, Specialist) */}
                    <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm border-b border-gray-200">
                      {type}
        </div>

                    {/* Level Groups (e.g., Foundation, Advanced) */}
                    {Object.entries(typeLevels).map(([level, levelCourses]) => (
                      <div key={`${type}-${level}`}>
                        {/* Level Header */}
                        <div className="px-6 py-1 bg-gray-100 font-medium text-gray-600 text-xs border-b border-gray-100">
                          {level}
          </div>
          
                        {/* Individual Courses */}
                        {levelCourses.map((course, index) => {
                          // Clean up course name by removing redundant prefixes and subheadings
                          const cleanCourseName = (name) => {
                            let cleanName = name;
                            
                            // Remove only section headings and subheadings, not individual words that could be part of course names
                            const prefixesToRemove = [
                              'Core Foundation ',
                              'Core Advanced ',
                              'Specialist Technologies & Approaches ',
                              'Specialist Quality Characteristics & Test Levels ',
                              'Specialist Testing in Particular Domains ',
                              'Expert Test Management ',
                              'Expert Improving the Testing Process ',  // With "the"
                              'Expert Improving Testing Process ',      // Without "the"
                              'Istqb Certified Tester ',
                              'ISTQB Certified Tester ',
                              'Certified Tester ',
                              'Advanced Level ',
                              'Foundation Level ',
                              'ISTQB '
                            ];
                            
                            // Apply prefix removal iteratively (some names might have multiple prefixes)
                            let previousLength;
                            do {
                              previousLength = cleanName.length;
                              prefixesToRemove.forEach(prefix => {
                                if (cleanName.startsWith(prefix)) {
                                  cleanName = cleanName.substring(prefix.length);
                                }
                              });
                            } while (cleanName.length < previousLength && cleanName.length > 0);
                            
                            // Clean up any double spaces and trim
                            cleanName = cleanName.replace(/\s+/g, ' ').trim();
                            
                            // If the name becomes too short or empty, use original name
                            if (cleanName.length < 3) {
                              cleanName = name;
                            }
                            
                            return cleanName;
                          };
                          
                          return (
                            <div
                              key={`${type}-${level}-${index}`}
                              className="px-8 py-2 cursor-pointer hover:bg-gray-50"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                                const selectedCourse = course.name.trim();
                                setCourseSearch(selectedCourse);
                                setCourseOpen(false);
                              }}
                            >
                              {cleanCourseName(course.name)}
                              {course.acronym && (
                                <span className="text-gray-500 text-sm ml-2">
                                  ({course.acronym})
                                </span>
                              )}
                  </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        {/* Accreditation Type Search */}
        <div className="relative" ref={materialTypeRef} style={{ zIndex: materialTypeOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Accreditation type..."
              value={getMaterialTypeDisplayValue(materialTypeSearch)}
              onChange={(e) => {
                const value = e.target.value;
                // If user is typing a friendly label, try to map it back to API value
                let apiValue = value;
                if (value === 'Training Provider(TP)') apiValue = 'TP';
                else if (value === 'Training Material Owner(TMO)') apiValue = 'TMO';
                else if (value === 'Training Material Owner and Training Provider(TMO&TP)') apiValue = 'TMO&TP';
                else if (value.includes('Training Provider(TP)') && !value.includes('Training Material Owner')) apiValue = 'TP';
                else if (value.includes('Training Material Owner(TMO)') && !value.includes('Training Provider')) apiValue = 'TMO';
                else if (value.includes('Training Material Owner and Training Provider(TMO&TP)')) apiValue = 'TMO&TP';
                
                setMaterialTypeSearch(apiValue);
                setMaterialTypeOpen(true);
              }}
              onFocus={() => {
                setMaterialTypeOpen(true);
              }}
            />
            <ClearButton 
              show={materialTypeSearch !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setMaterialTypeSearch("");
                setMaterialTypeOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                setMaterialTypeOpen(!materialTypeOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${materialTypeOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {materialTypeOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {providers.length === 0 ? (
                <DropdownLoader />
              ) : (
                filteredMaterialTypes.map((type, index) => (
                                      <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMaterialTypeSearch(type);
                      setMaterialTypeOpen(false);
                    }}
                  >
                    {getMaterialTypeLabel(type)}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Board, Provider and Training Category search row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* Board Search */}
        <div className="relative" ref={boardRef} style={{ zIndex: boardOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Board..."
              value={boardSearch}
              onChange={(e) => {
                const value = e.target.value;
                setBoardSearch(value);
                setBoardOpen(true);
              }}
              onFocus={() => {
                setBoardOpen(true);
              }}
            />
            <ClearButton 
              show={boardSearch !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setBoardSearch("");
                setBoardOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                setBoardOpen(!boardOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${boardOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {boardOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {boards.length === 0 ? (
                <div className="px-4 py-2 text-gray-500">
                  Loading boards... (Debug: {providers.length} providers loaded)
                </div>
              ) : (
                filteredBoards.map((board, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setBoardSearch(board);
                      setBoardOpen(false);
                      setTimeout(() => {
                      }, 0);
                    }}
                  >
                    {board}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Provider Search */}
        <div className="relative" ref={providerSearchRef} style={{ zIndex: isOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Training providers..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
              }}
            />
            <ClearButton 
              show={searchTerm !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm("");
                setIsOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                console.log("Desktop - Toggle provider dropdown button clicked");
                setIsOpen(!isOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {isOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {providers.length === 0 ? (
                <DropdownLoader />
              ) : filteredProviders.length > 0 ? (
                filteredProviders.map((provider, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchTerm(provider.name);
                      setIsOpen(false);
                    }}
                  >
                    {provider.name}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-gray-500">No matching providers found</div>
              )}
            </div>
          )}
        </div>

        {/* Training Category Search */}
        <div className="relative" ref={trainingCategoryRef} style={{ zIndex: trainingCategoryOpen ? 50 : 1 }}>
          <div className="relative">
            <input
              type="text"
              className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Training type..."
              value={getTrainingTypeDisplayValue(trainingCategorySearch)}
              onChange={(e) => {
                const value = e.target.value;
                // If user is typing a friendly label, try to map it back to API value
                let apiValue = value;
                if (value === 'Instructor led - Online') apiValue = 'online';
                else if (value === 'Instructor led - Onsite') apiValue = 'onsite';
                else if (value === 'eLearning') apiValue = 'elearning';
                else if (value.includes('Instructor led - Online')) apiValue = 'online';
                else if (value.includes('Instructor led - Onsite')) apiValue = 'onsite';
                else if (value.includes('eLearning')) apiValue = 'elearning';
                
                setTrainingCategorySearch(apiValue);
                setTrainingCategoryOpen(true);
              }}
              onFocus={() => {
                setTrainingCategoryOpen(true);
              }}
            />
            <ClearButton 
              show={trainingCategorySearch !== ""} 
              onClick={(e) => {
                e.stopPropagation();
                setTrainingCategorySearch("");
                setTrainingCategoryOpen(false);
              }} 
            />
            <button 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              onClick={(e) => {
                e.stopPropagation();
                setTrainingCategoryOpen(!trainingCategoryOpen);
              }}
              title="Toggle dropdown"
            >
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${trainingCategoryOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
          
          {trainingCategoryOpen && (
            <div 
              className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {providers.length === 0 ? (
                <DropdownLoader />
              ) : (
                filteredTrainingCategories.map((category, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTrainingCategorySearch(category);
                      setTrainingCategoryOpen(false);
                      setTimeout(() => {
                      }, 0);
                    }}
                  >
                    {getTrainingTypeLabel(category)}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search and Clear buttons row */}
      <div className="w-full flex items-center mb-6">
        <button
          onClick={handleSearch}
          style={{ 
            backgroundColor: '#c43e4b',
            color: 'white',
            padding: '0.5rem 1.5rem',
            borderRadius: '0.5rem',
            transition: 'background-color 200ms'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#a8363f'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#c43e4b'}
        >
          Search
        </button>

        <button
          onClick={clearAllSearches}
          className="ml-auto text-gray-600 hover:text-gray-800 px-6 py-2"
          style={{ float: 'right' }}
        >
          Clear All
        </button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div id="mobile-dropdowns" style={{ display: isMobileLayout ? 'block' : 'none' }}>
        <div className="p-6 rounded-lg mb-6">
          <div 
            style={{ 
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '1rem',
              width: '100%'
            }}
          >
            {/* Country Search */}
            <div 
              className="relative" 
              ref={countryRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Countries of Operation"
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value)
                    setCountryOpen(true)
                  }}
                  onFocus={() => setCountryOpen(true)}
                />
                <ClearButton 
                  show={countrySearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setCountrySearch("")
                    setCountryOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setCountryOpen(!countryOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {countryOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {providers.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredCountries.map((country, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setCountrySearch(country)
                        setCountryOpen(false)
                      }}
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Language Search */}
            <div 
              className="relative" 
              ref={languageRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Language..."
                  value={languageSearch}
                  onChange={(e) => {
                    setLanguageSearch(e.target.value)
                    setLanguageOpen(true)
                  }}
                  onFocus={() => setLanguageOpen(true)}
                />
                <ClearButton 
                  show={languageSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setLanguageSearch("")
                    setLanguageOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setLanguageOpen(!languageOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${languageOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {languageOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {providers.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredLanguages.map((language, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setLanguageSearch(language)
                        setLanguageOpen(false)
                      }}
                    >
                      {language}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Course Search */}
            <div 
              className="relative" 
              ref={courseRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Course..."
                  value={courseSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCourseSearch(value);
                    setCourseOpen(true);
                  }}
                  onFocus={() => {
                    setCourseOpen(true);
                  }}
                />
                <ClearButton 
                  show={courseSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setCourseSearch("");
                    setCourseOpen(false);
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCourseOpen(!courseOpen);
                  }}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${courseOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
                             {courseOpen && (
                 <div 
                   className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                   onClick={(e) => e.stopPropagation()}
                   onMouseDown={(e) => e.stopPropagation()}
                 >
                  {providers.length === 0 ? (
                    <DropdownLoader />
                  ) : (() => {
                    const groupedCourses = groupCoursesForDropdown(filteredCourseObjects);
                    
                    // If no groups, show simple list fallback
                    if (Object.keys(groupedCourses).length === 0) {
                      return filteredCourseObjects.sort((a, b) => {
                    // Clean course names for proper sorting
                    const cleanName = (name) => {
                      let cleanName = name;
                      const prefixesToRemove = [
                        'Core Foundation ', 'Core Advanced ', 'Specialist Technologies & Approaches ',
                        'Specialist Quality Characteristics & Test Levels ', 'Specialist Testing in Particular Domains ',
                        'Expert Test Management ', 'Expert Improving Testing Process ', 'Istqb Certified Tester ',
                        'ISTQB Certified Tester ', 'Certified Tester ', 'Advanced Level ', 'Foundation Level ', 'ISTQB '
                      ];
                      let previousLength;
                      do {
                        previousLength = cleanName.length;
                        prefixesToRemove.forEach(prefix => {
                          if (cleanName.startsWith(prefix)) {
                            cleanName = cleanName.substring(prefix.length);
                          }
                        });
                      } while (cleanName.length < previousLength && cleanName.length > 0);
                      cleanName = cleanName.replace(/\s+/g, ' ').trim();
                      return cleanName.length < 3 ? name : cleanName;
                    };
                    return cleanName(a.name).localeCompare(cleanName(b.name));
                  }).map((course, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const selectedCourse = course.name.trim();
                            setCourseSearch(selectedCourse);
                            setCourseOpen(false);
                          }}
                        >
                          {course.name}
                          {course.acronym && (
                            <span className="text-gray-500 text-sm ml-2">
                              ({course.acronym})
                            </span>
                          )}
                        </div>
                      ));
                    }
                    
                    return Object.entries(groupedCourses).map(([type, typeLevels]) => (
                      <div key={type}>
                        {/* Type Header (e.g., Core, Specialist) */}
                        <div className="px-4 py-2 bg-gray-50 font-semibold text-gray-700 text-sm border-b border-gray-200">
                          {type}
                        </div>
                        
                        {/* Level Groups (e.g., Foundation, Advanced) */}
                        {Object.entries(typeLevels).map(([level, levelCourses]) => (
                          <div key={`${type}-${level}`}>
                            {/* Level Header */}
                            <div className="px-6 py-1 bg-gray-100 font-medium text-gray-600 text-xs border-b border-gray-100">
                              {level}
                            </div>
                            
                            {/* Individual Courses */}
                            {levelCourses.map((course, index) => {
                              // Clean up course name by removing redundant prefixes and subheadings
                              const cleanCourseName = (name) => {
                                let cleanName = name;
                                
                                // Remove only section headings and subheadings, not individual words that could be part of course names
                                const prefixesToRemove = [
                                  'Core Foundation ',
                                  'Core Advanced ',
                                  'Specialist Technologies & Approaches ',
                                  'Specialist Quality Characteristics & Test Levels ',
                                  'Specialist Testing in Particular Domains ',
                                  'Expert Test Management ',
                                  'Expert Improving the Testing Process ',  // With "the"
                                  'Expert Improving Testing Process ',      // Without "the"
                                  'Istqb Certified Tester ',
                                  'ISTQB Certified Tester ',
                                  'Certified Tester ',
                                  'Advanced Level ',
                                  'Foundation Level ',
                                  'ISTQB '
                                ];
                                
                                // Apply prefix removal iteratively (some names might have multiple prefixes)
                                let previousLength;
                                do {
                                  previousLength = cleanName.length;
                                  prefixesToRemove.forEach(prefix => {
                                    if (cleanName.startsWith(prefix)) {
                                      cleanName = cleanName.substring(prefix.length);
                                    }
                                  });
                                } while (cleanName.length < previousLength && cleanName.length > 0);
                                
                                // Clean up any double spaces and trim
                                cleanName = cleanName.replace(/\s+/g, ' ').trim();
                                
                                // If the name becomes too short or empty, use original name
                                if (cleanName.length < 3) {
                                  cleanName = name;
                                }
                                
                                return cleanName;
                              };
                              
                              return (
                                <div
                                  key={`${type}-${level}-${index}`}
                                  className="px-8 py-2 cursor-pointer hover:bg-gray-50"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const selectedCourse = course.name.trim();
                                    setCourseSearch(selectedCourse);
                                    setCourseOpen(false);
                                  }}
                                >
                                  {cleanCourseName(course.name)}
                                  {course.acronym && (
                                  <span className="text-gray-500 text-sm ml-2">
                                      ({course.acronym})
                                  </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Board Search */}
            <div 
              className="relative" 
              ref={boardRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Board..."
                  value={boardSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBoardSearch(value);
                    setBoardOpen(true);
                  }}
                  onFocus={() => {
                    setBoardOpen(true);
                  }}
                />
                <ClearButton 
                  show={boardSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setBoardSearch("");
                    setBoardOpen(false);
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBoardOpen(!boardOpen);
                  }}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${boardOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {boardOpen && (
                <div 
                  className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {boards.length === 0 ? (
                    <div className="px-4 py-2 text-gray-500">
                      Loading boards... (Debug: {providers.length} providers loaded)
                    </div>
                  ) : (
                    filteredBoards.map((board, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setBoardSearch(board);
                          setBoardOpen(false);
                          setTimeout(() => {
                          }, 0);
                        }}
                      >
                        {board}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Provider Search */}
            <div 
              className="relative" 
              ref={providerSearchRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Training providers..."
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    setIsOpen(true);
                  }}
                  onFocus={() => {
                    setIsOpen(true);
                  }}
                />
                <ClearButton 
                  show={searchTerm !== ""} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm("");
                    setIsOpen(false);
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Mobile - Toggle provider dropdown button clicked");
                    setIsOpen(!isOpen);
                  }}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {isOpen && (
                <div 
                  className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {providers.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredProviders.length > 0 ? (
                    filteredProviders.map((provider, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSearchTerm(provider.name);
                          setIsOpen(false);
                        }}
                      >
                        {provider.name}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No matching providers found</div>
                  )}
                </div>
              )}
            </div>

            {/* Training Category Search */}
            <div 
              className="relative" 
              ref={trainingCategoryRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Training type..."
                  value={getTrainingTypeDisplayValue(trainingCategorySearch)}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If user is typing a friendly label, try to map it back to API value
                    let apiValue = value;
                    if (value === 'Instructor led - Online') apiValue = 'online';
                    else if (value === 'Instructor led - Onsite') apiValue = 'onsite';
                    else if (value === 'eLearning') apiValue = 'elearning';
                    else if (value.includes('Instructor led - Online')) apiValue = 'online';
                    else if (value.includes('Instructor led - Onsite')) apiValue = 'onsite';
                    else if (value.includes('eLearning')) apiValue = 'elearning';
                    
                    setTrainingCategorySearch(apiValue);
                    setTrainingCategoryOpen(true);
                  }}
                  onFocus={() => {
                    setTrainingCategoryOpen(true);
                  }}
                />
                <ClearButton 
                  show={trainingCategorySearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrainingCategorySearch("");
                    setTrainingCategoryOpen(false);
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrainingCategoryOpen(!trainingCategoryOpen);
                  }}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${trainingCategoryOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {trainingCategoryOpen && (
                <div 
                  className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {providers.length === 0 ? (
                    <DropdownLoader />
                  ) : (
                    filteredTrainingCategories.map((category, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTrainingCategorySearch(category);
                          setTrainingCategoryOpen(false);
                          setTimeout(() => {
                          }, 0);
                        }}
                      >
                        {getTrainingTypeLabel(category)}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Accreditation Type Search */}
            <div 
              className="relative" 
              ref={materialTypeRef}
              style={{ width: '100%' }}
            >
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Accreditation type..."
                  value={getMaterialTypeDisplayValue(materialTypeSearch)}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If user is typing a friendly label, try to map it back to API value
                    let apiValue = value;
                    if (value === 'Training Provider(TP)') apiValue = 'TP';
                    else if (value === 'Training Material Owner(TMO)') apiValue = 'TMO';
                    else if (value === 'Training Material Owner and Training Provider(TMO&TP)') apiValue = 'TMO&TP';
                    else if (value.includes('Training Provider(TP)') && !value.includes('Training Material Owner')) apiValue = 'TP';
                    else if (value.includes('Training Material Owner(TMO)') && !value.includes('Training Provider')) apiValue = 'TMO';
                    else if (value.includes('Training Material Owner and Training Provider(TMO&TP)')) apiValue = 'TMO&TP';
                    
                    setMaterialTypeSearch(apiValue);
                    setMaterialTypeOpen(true);
                  }}
                  onFocus={() => {
                    setMaterialTypeOpen(true);
                  }}
                />
                <ClearButton 
                  show={materialTypeSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setMaterialTypeSearch("");
                    setMaterialTypeOpen(false);
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMaterialTypeOpen(!materialTypeOpen);
                  }}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${materialTypeOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {materialTypeOpen && (
                <div 
                  className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {providers.length === 0 ? (
                    <DropdownLoader />
                  ) : (
                    filteredMaterialTypes.map((type, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setMaterialTypeSearch(type);
                          setMaterialTypeOpen(false);
                          setTimeout(() => {
                          }, 0);
                        }}
                      >
                        {getMaterialTypeLabel(type)}
                      </div>
                    ))
                  )}
                                 </div>
               )}
             </div>
               </div>

               {/* Mobile Search and Clear buttons row */}
               <div className="w-full flex items-center mt-6">
                 <button
                   onClick={handleSearch}
                   style={{ 
                     backgroundColor: '#c43e4b',
                     color: 'white',
                     padding: '0.5rem 1.5rem',
                     borderRadius: '0.5rem',
                     transition: 'background-color 200ms'
                   }}
                   onMouseOver={(e) => e.target.style.backgroundColor = '#a8363f'}
                   onMouseOut={(e) => e.target.style.backgroundColor = '#c43e4b'}
                 >
                   Search
                 </button>

                 <button
                   onClick={clearAllSearches}
                   className="ml-auto text-gray-600 hover:text-gray-800 px-6 py-2"
                 >
                   Clear All
                 </button>
               </div>
                    </div>
            </div>

      {/* Display results */}
      {searchResults.length > 0 && (
        <div className="mt-6">
          {/* {countrySearch && (
            <div className="bg-blue-50 px-4 py-2 mb-4 rounded-lg text-sm text-blue-700">
              <strong>Prioritized Results:</strong> Providers with {courseSearch ? `"${courseSearch}" course` : (trainingCategorySearch ? `"${trainingCategorySearch}" courses` : 'courses')} that {courseSearch ? 'is' : 'are'} locally accredited in "{countrySearch}" shown first.
            </div>
          )} */}
          <div className="text-sm text-gray-600 mb-4">
            Found {searchResults.length} training provider{searchResults.length !== 1 ? 's' : ''}
            </div>

          {/* Desktop Results Table */}
          <div id="desktop-results" style={{ display: isMobileLayout ? 'none' : 'block' }} className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Training Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Info</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {searchResults.map((provider, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600 max-w-xs truncate"
                         onClick={() => {
                           setSelectedProvider(provider)
                           setShowModal(true)
                         }}
                         title={provider.name}
                    >
                      {provider.name.length > 50 ? `${provider.name.substring(0, 35)}...` : provider.name}
                    </div>
                      {/* {countrySearch && hasLocalAccreditation(provider, countrySearch, courseSearch) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Local Accreditation
                        </span>
                      )} */}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      let trainingCategories = [];
                      
                      // If searching for a specific course, only show training types for that course
                      if (courseSearch && courseSearch.trim() !== '') {
                        const matchingCourse = provider.courses.find(course => 
                          course.name && course.name.toLowerCase().trim() === courseSearch.toLowerCase().trim()
                        );
                        
                        if (matchingCourse && matchingCourse.trainingCategories) {
                          trainingCategories = matchingCourse.trainingCategories
                            .map(category => category.name)
                            .filter(name => name)
                            .slice(0, 3);
                        }
                      } else {
                        // If not searching for specific course, show all unique training categories
                        trainingCategories = provider.courses
                          .flatMap(course => course.trainingCategories || [])
                          .map(category => category.name)
                          .filter((name, index, self) => name && self.indexOf(name) === index)
                          .slice(0, 3);
                      }
                      
                      if (trainingCategories.length === 0) {
                        return <span className="text-gray-400 italic">Not specified</span>;
                      }
                      
                      return (
                        <div className="space-y-1">
                          {trainingCategories.map((category, idx) => (
                            <div key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded inline-block mr-1 mb-1">
                              {getTrainingTypeLabel(category)}
                            </div>
                          ))}
                          {(!courseSearch || courseSearch.trim() === '') && provider.courses.flatMap(course => course.trainingCategories || []).map(category => category.name).filter((name, index, self) => name && self.indexOf(name) === index).length > 3 && (
                            <div className="text-xs text-gray-500">+{provider.courses.flatMap(course => course.trainingCategories || []).map(category => category.name).filter((name, index, self) => name && self.indexOf(name) === index).length - 3} more</div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate" title={provider.email}>
                    {provider.email.length > 35 ? `${provider.email.substring(0, 35)}...` : provider.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => {
                        setSelectedProvider(provider)
                        setShowModal(true)
                      }}
                      className="text-gray-600 hover:text-gray-900"
                      title="More Information"
                    >
                      <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {provider.website ? (
                    <a
                      href={provider.website && typeof provider.website === 'string' && provider.website.startsWith('http') 
                        ? provider.website 
                        : provider.website && typeof provider.website === 'string'
                          ? `https://${provider.website}`
                          : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-custom-red hover:bg-custom-red-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-red"
                    >
                      Go
                    </a>
                    ) : (
                      <span className="text-sm text-gray-500 italic">Not specified</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

          {/* Mobile Results Cards */}
          <div id="mobile-results" style={{ display: isMobileLayout ? 'block' : 'none' }} className="mt-6">
            <div>
              {searchResults.map((provider, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 
                      className="text-lg font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                      onClick={() => {
                        setSelectedProvider(provider)
                        setShowModal(true)
                      }}
                    >
                      {provider.name}
                    </h3>
                    {provider.website ? (
                    <a
                      href={provider.website && typeof provider.website === 'string' && provider.website.startsWith('http') 
                        ? provider.website 
                        : provider.website && typeof provider.website === 'string'
                          ? `https://${provider.website}`
                          : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-custom-red hover:bg-custom-red-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-red"
                    >
                      Website
                    </a>
                    ) : (
                      <span className="text-sm text-gray-500 italic">Not specified</span>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${provider.email}`} className="hover:text-blue-600 hover:underline">{provider.email}</a>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-4 h-4 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          let trainingCategories = [];
                          
                          // If searching for a specific course, only show training types for that course
                          if (courseSearch && courseSearch.trim() !== '') {
                            const matchingCourse = provider.courses.find(course => 
                              course.name && course.name.toLowerCase().trim() === courseSearch.toLowerCase().trim()
                            );
                            
                            if (matchingCourse && matchingCourse.trainingCategories) {
                              trainingCategories = matchingCourse.trainingCategories
                                .map(category => category.name)
                                .filter(name => name)
                                .slice(0, 3);
                            }
                          } else {
                            // If not searching for specific course, show all unique training categories
                            trainingCategories = provider.courses
                              .flatMap(course => course.trainingCategories || [])
                              .map(category => category.name)
                              .filter((name, index, self) => name && self.indexOf(name) === index)
                              .slice(0, 3);
                          }
                          
                          if (trainingCategories.length === 0) {
                            return <span className="text-gray-400 italic">Training type Not specified</span>;
                          }
                          
                          return (
                            <>
                              {trainingCategories.map((category, idx) => (
                                <div key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  {getTrainingTypeLabel(category)}
                                </div>
                              ))}
                              {(!courseSearch || courseSearch.trim() === '') && provider.courses.flatMap(course => course.trainingCategories || []).map(category => category.name).filter((name, index, self) => name && self.indexOf(name) === index).length > 3 && (
                                <div className="text-xs text-gray-500">+{provider.courses.flatMap(course => course.trainingCategories || []).map(category => category.name).filter((name, index, self) => name && self.indexOf(name) === index).length - 3} more</div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {/* {countrySearch && hasLocalAccreditation(provider, countrySearch, courseSearch) && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Local Accreditation
                      </span>
                    </div>
                  )} */}
                  
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => {
                        setSelectedProvider(provider)
                        setShowModal(true)
                      }}
                      className="text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      <span className="mr-1">More Info</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && selectedProvider && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-50 p-4">
          <div className={`relative top-20 mx-auto border shadow-lg rounded-md bg-white ${isMobileLayout ? 'mx-4' : 'w-full max-w-2xl'}`}>
            {/* Fixed Header */}
            <div className="sticky top-0 bg-white p-4 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold" style={{ color: '#215184' }}>{selectedProvider.name}</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className={`p-4 max-h-[calc(100vh-180px)] overflow-y-auto ${isMobileLayout ? 'pb-6' : ''}`}>
              <div className="text-sm">
                <div className="mb-4">
                  <h4 className="font-semibold mb-2 text-gray-700">Training Provider contact details (Main Offices)</h4>
                  <div className="grid grid-cols-2 gap-2 text-gray-600">
                    <p className="col-span-2"><span className="font-bold">Name:</span> {selectedProvider.name}</p>
                    <p className="col-span-2"><span className="font-bold">Reference Person:</span> {selectedProvider.referencePerson}</p>
                    <p className="col-span-2"><span className="font-bold">Address:</span> {selectedProvider.address}</p>
                    <p><span className="font-bold">City:</span> {selectedProvider.city}</p>
                    <p><span className="font-bold">Headquarters:</span> {selectedProvider.country}</p>
                    
                    <p><span className="font-bold">E-Mail:</span> <a href={`mailto:${selectedProvider.email}`} className="text-blue-600 hover:text-blue-800 hover:underline">{selectedProvider.email}</a></p>
                    <p><span className="font-bold">Web Site:</span> 
                      {selectedProvider.website ? (
                        <a 
                          href={selectedProvider.website.startsWith('http') ? selectedProvider.website : `https://${selectedProvider.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                        >
                          {selectedProvider.website}
                        </a>
                      ) : (
                        <span className="text-gray-400 ml-1 italic">Not specified</span>
                      )}
                    </p>
                  </div>
                </div>

                {renderCertifications(selectedProvider)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dev Panel */}
      <DevPanel 
        isOpen={showDevPanel}
        onClose={() => setShowDevPanel(false)}
        data={rawXmlData}
      />
        </>
      )}
    </div>
  )
}